import { useState, useEffect, useRef } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

// ─── API CONFIG ──────────────────────────────────────────────────────────────
const API_BASE = "https://thinkwealthadvisory.in/api";

async function apiFetch(path, options = {}, token = null) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (res.status === 401) {
    // Token expired or invalid — clear session and redirect to login
    localStorage.removeItem("tw_auth");
    window.location.href = "/";
    return;
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Request failed");
  }
  return res.json();
}

// ─── MOBILE HOOK ─────────────────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ─── THEME ───────────────────────────────────────────────────────────────────
const T = {
  bg: "var(--color-background-primary, #ffffff)",
  bg2: "var(--color-background-secondary, #f5f5f5)",
  bg3: "var(--color-background-tertiary, #eeeeee)",
  text: "var(--color-text-primary, #111111)",
  text2: "var(--color-text-secondary, #666666)",
  text3: "var(--color-text-tertiary, #999999)",
  border: "var(--color-border-tertiary, #e0e0e0)",
  border2: "var(--color-border-secondary, #cccccc)",
  green: "#1D9E75", greenBg: "#E1F5EE", greenText: "#085041",
  red: "#E24B4A", redBg: "#FCEBEB", redText: "#501313",
  blue: "#378ADD", blueBg: "#E6F1FB", blueText: "#042C53",
  amber: "#BA7517", amberBg: "#FAEEDA", amberText: "#412402",
};

const fmt = (n, d = 0) => n == null ? "—" : n.toLocaleString("en-IN", { maximumFractionDigits: d });
const fmtL = (n) => n >= 10000000 ? `₹${(n / 10000000).toFixed(2)} Cr` : n >= 100000 ? `₹${(n / 100000).toFixed(1)}L` : `₹${fmt(n)}`;
const fmtPct = (n) => `${n >= 0 ? "+" : ""}${n?.toFixed(2)}%`;

function Badge({ type, children }) {
  const styles = {
    green: { background: T.greenBg, color: T.greenText },
    red: { background: T.redBg, color: T.redText },
    amber: { background: T.amberBg, color: T.amberText },
    blue: { background: T.blueBg, color: T.blueText },
    gray: { background: T.bg2, color: T.text2 },
  };
  return <span style={{ ...styles[type] || styles.gray, padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 500, display: "inline-block" }}>{children}</span>;
}

function Card({ children, style = {} }) {
  return <div style={{ background: T.bg, border: `0.5px solid ${T.border}`, borderRadius: 12, padding: 16, ...style }}>{children}</div>;
}

function MetricCard({ label, value, sub, subType = "gray" }) {
  return (
    <div style={{ background: T.bg2, borderRadius: 8, padding: 14 }}>
      <div style={{ fontSize: 12, color: T.text2, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 500, color: T.text, marginBottom: 4 }}>{value}</div>
      {sub && <Badge type={subType}>{sub}</Badge>}
    </div>
  );
}

function Avatar({ name, size = 32, color = "blue" }) {
  const initials = name?.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "?";
  const colors = { blue: [T.blueBg, T.blueText], green: [T.greenBg, T.greenText], amber: [T.amberBg, T.amberText] };
  const [bg, fg] = colors[color] || colors.blue;
  return <div style={{ width: size, height: size, borderRadius: "50%", background: bg, color: fg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.35, fontWeight: 500, flexShrink: 0 }}>{initials}</div>;
}

function Btn({ children, onClick, variant = "default", style = {}, disabled = false }) {
  const base = { padding: "9px 16px", borderRadius: 8, fontSize: 13, cursor: disabled ? "not-allowed" : "pointer", border: `0.5px solid ${T.border2}`, transition: "all .15s", fontWeight: 400, opacity: disabled ? .5 : 1, minHeight: 38 };
  const variants = {
    default: { background: "transparent", color: T.text },
    primary: { background: T.text, color: T.bg, border: `0.5px solid ${T.text}` },
    danger: { background: "transparent", color: T.red, border: `0.5px solid ${T.red}` },
  };
  return <button style={{ ...base, ...variants[variant] || variants.default, ...style }} onClick={onClick} disabled={disabled}>{children}</button>;
}

function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 120 }}>
      <div style={{ width: 28, height: 28, borderRadius: "50%", border: `2px solid ${T.border2}`, borderTopColor: T.text, animation: "spin .7s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} input,select,textarea{font-size:16px !important;box-sizing:border-box;}`}</style>
    </div>
  );
}

// ─── LOGIN / SIGNUP PAGE ──────────────────────────────────────────────────────
function LoginPage({ onLogin }) {
  const params = new URLSearchParams(window.location.search);
  const urlInvite = params.get("invite") || "";
  const urlEmail = params.get("email") || "";

  const [mode, setMode] = useState(urlInvite ? "signup" : "login");
  const [email, setEmail] = useState(urlEmail);
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [inviteToken] = useState(urlInvite);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [signedUp, setSignedUp] = useState(false);

  const handleLogin = async () => {
    setLoading(true); setError("");
    try {
      const formData = new URLSearchParams();
      formData.append("username", email);
      formData.append("password", password);
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData,
      });
      if (!res.ok) throw new Error("Invalid email or password");
      const data = await res.json();
      onLogin(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleSignup = async () => {
    setError("");
    if (!inviteToken) { setError("Sign-up requires an invite link from your advisor"); return; }
    if (!fullName.trim()) { setError("Please enter your full name"); return; }
    if (!email.trim()) { setError("Please enter your email"); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== confirmPassword) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ full_name: fullName, email, password, role: "client", invite_token: inviteToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Registration failed");
      setSignedUp(true);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const inputStyle = { width: "100%", padding: "10px 12px", border: `0.5px solid ${T.border2}`,
    borderRadius: 8, background: T.bg, color: T.text, fontSize: 16, boxSizing: "border-box" };

  const Brand = () => (
    <div style={{ textAlign: "center", marginBottom: 32 }}>
      <div style={{ marginBottom: 4 }}>
        <span style={{ fontSize: 28, fontWeight: 700, color: "#2196F3" }}>Think</span>
        <span style={{ fontSize: 28, fontWeight: 700, color: "#00B4A6" }}>Wealth</span>
        <div style={{ height: 3, background: "linear-gradient(to right, #2196F3, #00B4A6)", borderRadius: 2, marginTop: 4 }} />
      </div>
      <div style={{ fontSize: 13, color: T.text2, fontStyle: "italic", marginTop: 6 }}>Money matters, 'coz it shouldn't</div>
    </div>
  );

  if (signedUp) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: T.bg3, padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 380 }}>
        <Brand />
        <Card style={{ padding: 28, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🎉</div>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Account created!</div>
          <div style={{ fontSize: 13, color: T.text2, marginBottom: 24, lineHeight: 1.7 }}>
            Welcome to ThinkWealth. Your account is ready — please log in and complete your KYC to get started.
          </div>
          <Btn variant="primary" style={{ width: "100%", padding: "12px" }}
            onClick={() => { setMode("login"); setSignedUp(false); setPassword(""); setConfirmPassword(""); }}>
            Sign in now →
          </Btn>
        </Card>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: T.bg3, padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 380 }}>
        <Brand />

        {/* Mode toggle */}
        <div style={{ display: "flex", background: T.bg, borderRadius: 10, padding: 4, marginBottom: 20, border: `0.5px solid ${T.border}` }}>
          {[["login", "Sign in"], ["signup", "Create account"]].map(([m, label]) => (
            <button key={m} onClick={() => { setMode(m); setError(""); }}
              style={{ flex: 1, padding: "8px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 500, transition: "all 0.15s",
                background: mode === m ? T.text : "transparent",
                color: mode === m ? T.bg : T.text2 }}>
              {label}
            </button>
          ))}
        </div>

        <Card style={{ padding: 24 }}>
          {mode === "login" ? (
            <>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: T.text2, display: "block", marginBottom: 6 }}>Email</label>
                <input value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com"
                  onKeyDown={e => e.key === "Enter" && handleLogin()} style={inputStyle} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, color: T.text2, display: "block", marginBottom: 6 }}>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••"
                  onKeyDown={e => e.key === "Enter" && handleLogin()} style={inputStyle} />
              </div>
              {error && <div style={{ color: T.red, fontSize: 12, marginBottom: 12 }}>{error}</div>}
              <Btn variant="primary" style={{ width: "100%", padding: "12px" }} onClick={handleLogin} disabled={loading}>
                {loading ? "Signing in…" : "Sign in"}
              </Btn>
            </>
          ) : (
            <>
              {inviteToken ? (
                <div style={{ background: T.greenBg, border: `0.5px solid ${T.green}`, borderRadius: 8, padding: "10px 12px", marginBottom: 16, fontSize: 12, color: T.greenText }}>
                  Invite accepted for <b>{urlEmail}</b>. Set a password to create your account.
                </div>
              ) : (
                <div style={{ background: T.amberBg, border: `0.5px solid ${T.amber}`, borderRadius: 8, padding: "10px 12px", marginBottom: 16, fontSize: 12, color: T.amberText }}>
                  Sign-up is by invite only. Please use the invite link sent by your advisor.
                </div>
              )}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: T.text2, display: "block", marginBottom: 6 }}>Full name</label>
                <input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="As per PAN card"
                  style={inputStyle} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: T.text2, display: "block", marginBottom: 6 }}>Email</label>
                <input value={email} onChange={e => !urlEmail && setEmail(e.target.value)} placeholder="your@email.com"
                  style={{ ...inputStyle, background: urlEmail ? T.bg2 : T.bg, color: urlEmail ? T.text2 : T.text }} readOnly={!!urlEmail} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: T.text2, display: "block", marginBottom: 6 }}>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 8 characters"
                  style={inputStyle} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, color: T.text2, display: "block", marginBottom: 6 }}>Confirm password</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••"
                  onKeyDown={e => e.key === "Enter" && handleSignup()} style={inputStyle} />
              </div>
              {error && <div style={{ color: T.red, fontSize: 12, marginBottom: 12 }}>{error}</div>}
              <Btn variant="primary" style={{ width: "100%", padding: "12px" }} onClick={handleSignup} disabled={loading || !inviteToken}>
                {loading ? "Creating account…" : "Create account"}
              </Btn>
              <div style={{ fontSize: 11, color: T.text2, marginTop: 14, lineHeight: 1.6, textAlign: "center" }}>
                By signing up you agree to ThinkWealth's advisory terms. KYC verification required before investing.
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

// ─── MORNING BRIEF CARD ───────────────────────────────────────────────────────
function MorningBriefCard({ token, isMobile }) {
  const [brief, setBrief]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = () => {
    setLoading(true);
    apiFetch("/admin/morning-brief", {}, token)
      .then(d => setBrief(d))
      .catch(() => setBrief(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  // Re-fetch when user returns to this tab (handles overnight stale data)
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [token]);

  const generateNow = async () => {
    setGenerating(true);
    try {
      await apiFetch("/admin/morning-brief/generate", { method: "POST" }, token);
      load();
    } catch(e) { alert("Generation failed: " + e.message); }
    finally { setGenerating(false); }
  };

  const labelColor = (l) => {
    if (!l) return T.text2;
    if (l.includes("Strongly Bullish")) return T.green;
    if (l.includes("Mildly Bullish"))   return T.green;
    if (l.includes("Neutral"))          return T.blue;
    if (l.includes("Mildly Bearish"))   return T.amber;
    return T.red;
  };
  const labelBg = (l) => {
    if (!l) return T.bg2;
    if (l.includes("Bullish")) return T.greenBg;
    if (l.includes("Neutral")) return T.blueBg;
    if (l.includes("Mildly Bearish")) return T.amberBg;
    return T.redBg;
  };
  const labelFg = (l) => {
    if (!l) return T.text2;
    if (l.includes("Bullish")) return T.greenText;
    if (l.includes("Neutral")) return T.blueText;
    if (l.includes("Mildly Bearish")) return T.amberText;
    return T.redText;
  };
  const bulletColor = (b) => {
    if (!b) return T.text2;
    if (b.startsWith("BULLISH")) return T.green;
    if (b.startsWith("BEARISH")) return T.red;
    return T.amber;
  };
  const bulletBg = (b) => {
    if (!b) return T.bg2;
    if (b.startsWith("BULLISH")) return T.greenBg;
    if (b.startsWith("BEARISH")) return T.redBg;
    return T.amberBg;
  };
  const bulletTag = (b) => {
    if (!b) return "NEUTRAL";
    if (b.startsWith("BULLISH")) return "BULLISH";
    if (b.startsWith("BEARISH")) return "BEARISH";
    return "NEUTRAL";
  };
  const bulletText = (b) => {
    if (!b) return b;
    return b.replace(/^(BULLISH|BEARISH|NEUTRAL)\s*[\|:]\s*/i, "");
  };

  if (loading) return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13, color: T.text2, padding: "8px 0" }}>Loading morning brief…</div>
    </Card>
  );

  if (!brief?.available) return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Morning Brief</div>
          <div style={{ fontSize: 12, color: T.text2 }}>
            No brief yet today. Cron runs at 7:00 AM IST. Generate now?
          </div>
        </div>
        <Btn onClick={generateNow} disabled={generating} style={{ fontSize: 12 }}>
          {generating ? "Generating…" : "Generate now"}
        </Btn>
      </div>
    </Card>
  );

  const b = brief;
  const isStale = !b.is_today;

  return (
    <Card style={{ marginBottom: 16 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: expanded ? 16 : 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Morning Brief</div>
          {b.macro_label && (
            <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
              background: labelBg(b.macro_label), color: labelFg(b.macro_label) }}>
              {b.macro_label}
            </span>
          )}
          <span style={{ fontSize: 11, color: T.text3 }}>
            {isStale ? `⚠ ${b.brief_date}` : `Today · ${b.generated_at ? new Date(b.generated_at.replace(" ", "T") + "Z").toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) + " IST" : ""}`}
          </span>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Btn onClick={generateNow} disabled={generating} style={{ fontSize: 11 }}>
            {generating ? "…" : "↻ Refresh"}
          </Btn>
          <button onClick={() => setExpanded(e => !e)}
            style={{ background: "transparent", border: `0.5px solid ${T.border2}`,
              borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12, color: T.text2 }}>
            {expanded ? "▲" : "▼"}
          </button>
        </div>
      </div>

      {expanded && (
        <div>
          {/* News bullets */}
          {(b.news_bullets || []).length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: T.text3, textTransform: "uppercase",
                letterSpacing: ".05em", marginBottom: 8 }}>Market news · last 24h</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {(b.news_bullets || []).map((bullet, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 5px", borderRadius: 3,
                      background: bulletBg(bullet), color: bulletColor(bullet),
                      flexShrink: 0, marginTop: 1, letterSpacing: ".04em" }}>
                      {bulletTag(bullet)}
                    </span>
                    <span style={{ fontSize: 12, color: T.text, lineHeight: 1.5 }}>
                      {bulletText(bullet)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Divider */}
          <div style={{ height: 1, background: T.border, marginBottom: 14 }} />

          {/* Three text sections */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 14 }}>
            {/* Macro summary */}
            <div style={{ padding: "10px 12px", background: T.bg2, borderRadius: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 500, color: T.text3, textTransform: "uppercase",
                letterSpacing: ".05em", marginBottom: 6 }}>Global macro · 2-4 week view</div>
              <div style={{ fontSize: 12, color: T.text, lineHeight: 1.7 }}>{b.macro_summary}</div>
            </div>

            {/* Market snapshot */}
            <div style={{ padding: "10px 12px", background: T.bg2, borderRadius: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 500, color: T.text3, textTransform: "uppercase",
                letterSpacing: ".05em", marginBottom: 6 }}>India market snapshot · current</div>
              <div style={{ fontSize: 12, color: T.text, lineHeight: 1.7 }}>{b.market_snapshot}</div>
            </div>
          </div>

          {/* Trading view */}
          <div style={{ padding: "10px 12px", background: T.blueBg, borderRadius: 8,
            border: `0.5px solid ${T.blue}30`, marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 500, color: T.blueText, textTransform: "uppercase",
              letterSpacing: ".05em", marginBottom: 6 }}>Short-term trading view · 1-2 week horizon</div>
            <div style={{ fontSize: 12, color: T.text, lineHeight: 1.7 }}>{b.trading_view}</div>
          </div>

          {/* Hedge note */}
          <div style={{ padding: "10px 12px", borderRadius: 8,
            background: b.put_deploy || b.call_deploy ? T.greenBg : T.bg2,
            border: `0.5px solid ${b.put_deploy || b.call_deploy ? T.green : T.border}30` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 500,
                color: b.put_deploy || b.call_deploy ? T.greenText : T.text3,
                textTransform: "uppercase", letterSpacing: ".05em" }}>
                Hedge strategy · monthly horizon
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  { label: `Put sell ${b.put_deploy ? "DEPLOY" : "AVOID"}`, active: b.put_deploy },
                  { label: `Call sell ${b.call_deploy ? "DEPLOY" : "AVOID"}`, active: b.call_deploy },
                ].map((item, i) => (
                  <span key={i} style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4,
                    background: item.active ? T.greenBg : T.redBg,
                    color: item.active ? T.greenText : T.redText }}>
                    {item.label}
                  </span>
                ))}
              </div>
            </div>
            <div style={{ fontSize: 12, color: T.text, lineHeight: 1.7 }}>{b.hedge_note}</div>
          </div>
        </div>
      )}
    </Card>
  );
}

// ─── ADMIN DASHBOARD ─────────────────────────────────────────────────────────
function AdminDashboard({ token, isMobile }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [inviteModal, setInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [copied, setCopied] = useState(false);

  const generateInvite = async () => {
    setInviteError(""); setInviteLink("");
    if (!inviteEmail.trim()) { setInviteError("Enter an email address"); return; }
    setInviteLoading(true);
    try {
      const res = await apiFetch("/admin/create-invite", { method: "POST", body: JSON.stringify({ email: inviteEmail }) }, token);
      setInviteLink(res.invite_link);
    } catch(e) { setInviteError(e.message); }
    finally { setInviteLoading(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    apiFetch("/admin/dashboard/live", {}, token)
      .then(setData).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <Spinner />;
  if (error) return <div style={{ color: T.red, padding: 20 }}>Error: {error}</div>;
  if (!data) return null;

  const avatarColors = ["blue", "green", "amber"];

  return (
    <div>
      {/* Morning Brief — first thing admin sees every day */}
      <MorningBriefCard token={token} isMobile={isMobile} />

      {/* 4 metric cards — 2 cols on mobile, 4 on desktop */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: 12, marginBottom: 20 }}>
        <MetricCard label="Total AUM" value={fmtL(data.total_aum)} sub="+2.4% this month" subType="green" />
        <MetricCard label="Active clients" value={data.total_clients} sub={`${data.pending_rebalances} pending rebalance`} />
        <MetricCard label="Avg. XIRR" value={`${data.avg_xirr}%`} sub="vs Nifty 11.2%" subType="green" />
        <MetricCard label="Strategies" value="3" sub="2 active, 1 draft" />
      </div>

      {/* AUM chart — full width */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 14 }}>AUM trend</div>
        <ResponsiveContainer width="100%" height={160}>
          <AreaChart data={data.aum_series || []}>
            <defs>
              <linearGradient id="aumGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={T.blue} stopOpacity={0.15} />
                <stop offset="95%" stopColor={T.blue} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: T.text3 }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 10, fill: T.text3 }} tickLine={false} axisLine={false} tickFormatter={v => `₹${(v / 10000000).toFixed(1)}Cr`} />
            <Tooltip contentStyle={{ background: T.bg, border: `0.5px solid ${T.border}`, borderRadius: 8, fontSize: 12 }} formatter={v => [fmtL(v), "AUM"]} />
            <Area type="monotone" dataKey="aum" stroke={T.blue} strokeWidth={2} fill="url(#aumGrad)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      {/* Invite modal */}
      {inviteModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: T.bg, borderRadius: 12, padding: 28, width: "100%", maxWidth: 420, boxShadow: "0 8px 40px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Invite a client</div>
            <div style={{ fontSize: 12, color: T.text2, marginBottom: 20 }}>The invite link will be locked to this email address.</div>
            {!inviteLink ? (
              <>
                <label style={{ fontSize: 12, color: T.text2, display: "block", marginBottom: 6 }}>Client email</label>
                <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                  placeholder="friend@example.com" onKeyDown={e => e.key === "Enter" && generateInvite()}
                  style={{ width: "100%", padding: "10px 12px", border: `0.5px solid ${T.border2}`, borderRadius: 8, background: T.bg, color: T.text, fontSize: 15, boxSizing: "border-box", marginBottom: 12 }} />
                {inviteError && <div style={{ color: T.red, fontSize: 12, marginBottom: 10 }}>{inviteError}</div>}
                <div style={{ display: "flex", gap: 10 }}>
                  <Btn variant="primary" onClick={generateInvite} disabled={inviteLoading} style={{ flex: 1 }}>
                    {inviteLoading ? "Generating…" : "Generate invite link"}
                  </Btn>
                  <Btn onClick={() => { setInviteModal(false); setInviteEmail(""); setInviteLink(""); setInviteError(""); }}>Cancel</Btn>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 12, color: T.text2, marginBottom: 8 }}>Share this link with your client:</div>
                <div style={{ background: T.bg2, borderRadius: 8, padding: "10px 12px", fontSize: 11, color: T.text, wordBreak: "break-all", marginBottom: 14, border: `0.5px solid ${T.border}` }}>{inviteLink}</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <Btn variant="primary" onClick={copyLink} style={{ flex: 1 }}>{copied ? "Copied!" : "Copy link"}</Btn>
                  <Btn onClick={() => { setInviteModal(false); setInviteEmail(""); setInviteLink(""); setInviteError(""); }}>Done</Btn>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Clients — card list (no table) */}
      <Card>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 500 }}>Clients</div>
          <Btn variant="primary" onClick={() => { setInviteModal(true); setInviteLink(""); setInviteEmail(""); setInviteError(""); }}>+ Invite client</Btn>
        </div>
        {(data.clients || []).map((c, i) => {
          const gain = c.current_value - c.invested;
          return (
            <div key={c.client_id} style={{ padding: "14px 0", borderBottom: `0.5px solid ${T.border}` }}>
              {/* Row 1: avatar + name + status badge */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <Avatar name={c.client_name} color={avatarColors[i % 3]} size={36} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{c.client_name}</div>
                  <div style={{ fontSize: 11, color: T.text2, textTransform: "capitalize" }}>{c.portfolio_name} · {c.broker}</div>
                </div>
                <Badge type={c.rebalance_status === "pending" ? "amber" : "green"}>
                  {c.rebalance_status === "pending" ? "Pending" : "Active"}
                </Badge>
              </div>
              {/* Row 2: three inline stat pills */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <div style={{ background: T.bg2, borderRadius: 6, padding: "5px 10px", fontSize: 12 }}>
                  <span style={{ color: T.text2 }}>Invested </span><b>{fmtL(c.invested)}</b>
                </div>
                <div style={{ background: T.bg2, borderRadius: 6, padding: "5px 10px", fontSize: 12 }}>
                  <span style={{ color: T.text2 }}>Current </span><b>{fmtL(c.current_value)}</b>
                </div>
                <div style={{ background: gain >= 0 ? T.greenBg : T.redBg, borderRadius: 6, padding: "5px 10px", fontSize: 12, color: gain >= 0 ? T.greenText : T.redText, fontWeight: 500 }}>
                  {gain >= 0 ? "+" : ""}{fmtL(gain)}
                </div>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}
// ─── REPORTS SECTION ─────────────────────────────────────────────────────────
function ReportsSection({ token, isAdmin, portfolioId, userId }) {
  const [generating, setGenerating] = useState({});
  const [links, setLinks] = useState({});

  const generate = async (type) => {
    setGenerating(g => ({ ...g, [type]: true }));
    try {
      let path;
      if (type === "kyc")        path = `/reports/kyc-summary/${userId}`;
      if (type === "quarterly")  path = `/reports/quarterly/${portfolioId}`;
      if (type === "compliance") path = `/reports/compliance-audit`;
      const res = await apiFetch(path, {}, token);
      setLinks(l => ({ ...l, [type]: res.url }));
    } catch(e) { alert("Report generation failed: " + e.message); }
    finally { setGenerating(g => ({ ...g, [type]: false })); }
  };

  const allReports = [
    { type: "compliance", label: "Compliance Audit",  icon: "📋", desc: "SEBI checklist + full rebalance audit trail", adminOnly: true,  needsPortfolio: false },
    { type: "kyc",        label: "KYC Summary",       icon: "👤", desc: "KYC profile, documents, and agreement status", adminOnly: false, needsPortfolio: false, needsUser: true },
    { type: "quarterly",  label: "Performance Report",icon: "📈", desc: "Portfolio performance, holdings, cash flows",  adminOnly: false, needsPortfolio: true },
  ];

  const visible = allReports.filter(r => {
    if (r.adminOnly && !isAdmin) return false;
    if (r.needsUser && !userId) return false;
    if (r.needsPortfolio && !portfolioId) return false;
    return true;
  });

  return (
    <Card style={{ marginTop: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
        Reports
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {visible.map(r => (
          <div key={r.type} style={{ display: "flex", alignItems: "center", gap: 12,
            padding: "12px 14px", borderRadius: 10, border: `0.5px solid ${T.border}`, background: T.bg2 }}>
            <div style={{ fontSize: 20, flexShrink: 0 }}>{r.icon}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{r.label}</div>
              <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>{r.desc}</div>
            </div>
            <div style={{ flexShrink: 0 }}>
              {links[r.type] ? (
                <a href={links[r.type]} target="_blank" rel="noreferrer"
                  style={{ padding: "8px 14px", borderRadius: 8, background: T.green, color: "#fff",
                    fontSize: 12, fontWeight: 500, textDecoration: "none", display: "inline-block" }}>
                  Download ↓
                </a>
              ) : (
                <Btn onClick={() => generate(r.type)} disabled={generating[r.type]} style={{ fontSize: 12 }}>
                  {generating[r.type] ? "Generating…" : "Generate"}
                </Btn>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PortfolioAnalytics({ token, portfolioId, isMobile, auth }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!portfolioId) return;
    setLoading(true);
    apiFetch(`/portfolio/${portfolioId}/nav/live`, {}, token)
      .then(setData).catch(console.error).finally(() => setLoading(false));
  }, [token, portfolioId]);

  if (loading) return <Spinner />;
  if (!data) return <div style={{ padding: 20, color: T.text2 }}>No portfolio data available.</div>;

  const navSeries = data.nav_series || [];
  const latest = navSeries[navSeries.length - 1] || {};
  const currentValue = latest.total_value || 0;
  const investedValue = latest.invested_value || 0;
  const gain = currentValue - investedValue;
  const gainPct = investedValue ? ((gain / investedValue) * 100).toFixed(2) : 0;
  const trackingSince = navSeries[0]?.date || "today";

  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 4 }}>My portfolio</div>
      <div style={{ fontSize: 12, color: T.text2, marginBottom: 20, fontStyle: "italic" }}>
        Tracking since {trackingSince} — performance metrics will populate as history builds
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 12, marginBottom: 24 }}>
        <MetricCard label="Current value" value={fmtL(currentValue)} sub={gain >= 0 ? `+${fmtL(gain)} gain` : `${fmtL(gain)} loss`} subType={gain >= 0 ? "green" : "red"} />
        <MetricCard label="Total invested" value={fmtL(investedValue)} sub={`${gainPct}% return`} subType={gainPct >= 0 ? "green" : "red"} />
        <MetricCard label="NAV index" value={latest.nav_index ? latest.nav_index.toFixed(2) : "100.00"} sub="Base 100 from inception" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 12, marginBottom: 24 }}>
        <MetricCard label="XIRR" value="—" sub="Available after 30 days" />
        <MetricCard label="Sharpe ratio" value="—" sub="Available after 90 days" />
        <MetricCard label="Max drawdown" value="—" sub="Available after 90 days" />
      </div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 14 }}>Equity curve vs Nifty 50</div>
        <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8, background: T.bg2, borderRadius: 8 }}>
          <div style={{ fontSize: 24, color: T.text3 }}>◉</div>
          <div style={{ fontSize: 13, color: T.text2 }}>Equity curve will appear after 30 days of tracking</div>
          <div style={{ fontSize: 12, color: T.text3 }}>Tracking since {trackingSince}</div>
        </div>
      </Card>
      <Card>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 14 }}>Cash flow history</div>
        {(data.cash_flows || []).slice(-8).reverse().length === 0
          ? <div style={{ fontSize: 13, color: T.text2 }}>No cash flows recorded yet.</div>
          : (data.cash_flows || []).slice(-8).reverse().map((cf, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", borderBottom: `0.5px solid ${T.border}` }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: T.green }}>₹{fmt(cf.amount)}</div>
                <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>{cf.date}</div>
              </div>
              <Badge type={cf.type === "sip" ? "blue" : cf.type === "lumpsum" ? "amber" : "gray"}>{cf.type}</Badge>
            </div>
          ))
        }
      </Card>
      <ReportsSection token={token} isAdmin={false} portfolioId={portfolioId} userId={auth?.user_id} />
    </div>
  );
}
function ClientPortfolios({ token, onSelectPortfolio, isMobile }) {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/client/portfolios/live", {}, token)
      .then(d => setPortfolios(d.portfolios || [])).catch(console.error).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 20 }}>My portfolios</div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 16 }}>
        {portfolios.map(p => (
          <Card key={p.id} style={{ cursor: "pointer" }} onClick={() => onSelectPortfolio(p.id)}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{p.strategy_name}</div>
              <Badge type="green">Active</Badge>
            </div>
            <div style={{ fontSize: 22, fontWeight: 500, marginBottom: 2 }}>{fmtL(p.current_value)}</div>
            <div style={{ fontSize: 12, color: T.text2 }}>
              Gain: <span style={{ color: p.gain >= 0 ? T.green : T.red }}>{fmtL(p.gain)} ({fmtPct(p.gain_pct)})</span>
            </div>
            {p.rebalance_pending && (
              <div style={{ marginTop: 10, padding: "6px 10px", background: T.blueBg, borderRadius: 6, fontSize: 12, color: T.blueText }}>
                Rebalance ready — tap to view
              </div>
            )}
          </Card>
        ))}
        <div style={{ border: `2px dashed ${T.border2}`, borderRadius: 12, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 150, cursor: "pointer", color: T.text2 }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>+</div>
          <div style={{ fontSize: 13 }}>Add portfolio</div>
        </div>
      </div>
    </div>
  );
}

// ─── ADMIN: PUBLISH REBALANCE ────────────────────────────────────────────────
function AdminRebalancePage({ token, auth, isMobile }) {
  const [prevPortfolio, setPrevPortfolio] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loadingPrev, setLoadingPrev] = useState(true);
  const [newSymbol, setNewSymbol] = useState("");
  const [newWeight, setNewWeight] = useState("");
  const [newIsin, setNewIsin] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [published, setPublished] = useState(false);
  const [preview, setPreview] = useState(null);
  const [paperMode, setPaperMode] = useState(true);
  const [turnoverCap, setTurnoverCap] = useState("");

  // Fetch last published weights from DB on mount
  useEffect(() => {
    apiFetch("/admin/rebalance/latest-weights", {}, token)
      .then(data => {
        const prev = data.stocks || [];
        setPrevPortfolio(prev);
        setStocks(prev.map(s => ({ ...s, prev_weight: s.weight, isExit: false })));
      })
      .catch(e => console.error("Failed to load previous weights:", e))
      .finally(() => setLoadingPrev(false));
  }, [token]);

  const totalWeight = stocks.filter(s => !s.isExit).reduce((s, x) => s + (parseFloat(x.weight) || 0), 0);
  const exits = stocks.filter(s => s.isExit);
  const active = stocks.filter(s => !s.isExit);
  const newEntries = active.filter(s => !prevPortfolio.find(p => p.symbol === s.symbol));
  const changes = active.filter(s => {
    const prev = prevPortfolio.find(p => p.symbol === s.symbol);
    return prev && Math.abs(prev.weight - s.weight) > 0.05;
  });

  const searchSymbols = async (q) => {
    setNewSymbol(q); setNewIsin("");
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const res = await apiFetch(`/symbols/search?q=${encodeURIComponent(q)}`, {}, token);
      setSearchResults(res.results || []);
    } catch(e) { setSearchResults([]); }
  };

  const selectSymbol = (result) => { setNewSymbol(result.symbol); setNewIsin(result.isin); setSearchResults([]); };
  const addStock = () => {
    if (!newSymbol || !newWeight) return;
    if (stocks.find(s => s.symbol === newSymbol.toUpperCase())) { alert("Stock already in list"); return; }
    setStocks([...stocks, { symbol: newSymbol.toUpperCase(), isin: newIsin, weight: parseFloat(newWeight), prev_weight: 0, isExit: false }]);
    setNewSymbol(""); setNewWeight(""); setNewIsin(""); setSearchResults([]);
  };

  const toggleExit = (sym) => setStocks(stocks.map(s => s.symbol === sym ? { ...s, isExit: !s.isExit } : s));
  const updateWeight = (sym, val) => setStocks(stocks.map(s => s.symbol === sym ? { ...s, weight: parseFloat(val) || 0 } : s));
  const removeStock = (sym) => setStocks(stocks.filter(s => s.symbol !== sym));
  const buildPreview = () => setPreview({
    exits: exits.map(s => s.symbol),
    entries: newEntries.map(s => s.symbol),
    changes: changes.map(s => ({ symbol: s.symbol, from: s.prev_weight, to: s.weight })),
    total: totalWeight.toFixed(2),
  });

  if (published) {
    const exitSymbols = stocks.filter(s => s.isExit).map(s => s.symbol);
    const newSymbols = stocks.filter(s => !s.isExit && !prevPortfolio.find(p => p.symbol === s.symbol)).map(s => s.symbol);
    const today = new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" });
    const whatsappMsg = [
      "*ThinkWealth — " + today + " Rebalance Ready* 🎯",
      "",
      "Your portfolio has been updated for this month.",
      "",
      exitSymbols.length ? "❌ *Exit:* " + exitSymbols.join(", ") : "",
      newSymbols.length ? "✅ *New entry:* " + newSymbols.join(", ") : "",
      (!exitSymbols.length && !newSymbols.length) ? "↔️ *Weight adjustments only* — same stocks, rebalanced proportions" : "",
      "",
      "👉 Login to review & execute: https://thinkwealthadvisory.in",
      "",
      "_Connect your Zerodha account on the Execute Rebalance page. Your orders will be pre-filled for one-click execution._",
      "",
      "_— ThinkWealth Advisory_"
    ].filter(Boolean).join("\n");

    return (
      <div style={{ padding: "20px 0" }}>
        <div style={{ textAlign: "center", padding: "24px 20px" }}>
          <div style={{ fontSize: 36, marginBottom: 12, color: T.green }}>✓</div>
          <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 6 }}>Rebalance published</div>
          <div style={{ fontSize: 13, color: T.text2 }}>All clients have been notified by email. Order baskets are ready for execution.</div>
        </div>

        <Card style={{ marginTop: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>📱 WhatsApp Broadcast Message</div>
            <Btn style={{ fontSize: 11, padding: "4px 10px" }} onClick={() => {
              navigator.clipboard.writeText(whatsappMsg).then(() => alert("Copied to clipboard! Open WhatsApp Broadcast and paste."));
            }}>Copy</Btn>
          </div>
          <div style={{ background: T.bg2, borderRadius: 8, padding: "14px", fontSize: 13, lineHeight: 1.8,
            whiteSpace: "pre-wrap", fontFamily: "monospace", color: T.text, border: `0.5px solid ${T.border}` }}>
            {whatsappMsg}
          </div>
          <div style={{ fontSize: 11, color: T.text2, marginTop: 10, lineHeight: 1.6 }}>
            💡 <b>Send without revealing other clients:</b> Open WhatsApp → New Broadcast → add all client numbers → paste and send. Each client receives it as a private message.
          </div>
        </Card>
      </div>
    );
  }

  if (loadingPrev) return <Spinner />;

  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 4 }}>Publish rebalance</div>
      <div style={{ fontSize: 12, color: T.text2, marginBottom: 20 }}>Set target weights. Exits and entries are auto-detected.</div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: 12, marginBottom: 20 }}>
        {[[active.length, "Stocks"], [newEntries.length, "New entries"], [exits.length, "Full exits"], [totalWeight.toFixed(1) + "%", "Total weight"]].map(([v, l]) => (
          <div key={l} style={{ background: T.bg2, borderRadius: 8, padding: "12px 14px" }}>
            <div style={{ fontSize: 20, fontWeight: 500, color: totalWeight > 100.1 && l === "Total weight" ? T.red : T.text }}>{v}</div>
            <div style={{ fontSize: 12, color: T.text2, marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>Portfolio stocks & target weights</div>

        {/* Mobile-first: one row per stock, no table */}
        <div>
          {stocks.map((s) => {
            const prev = prevPortfolio.find(p => p.symbol === s.symbol);
            const isNew = !prev;
            const delta = prev ? (s.weight - prev.weight) : s.weight;
            return (
              <div key={s.symbol} style={{ padding: "12px 0", borderBottom: `0.5px solid ${T.border}`, opacity: s.isExit ? 0.4 : 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
                    {s.symbol}
                    {isNew && <Badge type="blue" style={{ marginLeft: 6 }}>New</Badge>}
                  </div>
                  {s.isExit
                    ? <Badge type="red">Exit</Badge>
                    : delta > 0.05 ? <Badge type="green">+{delta.toFixed(2)}%</Badge>
                    : delta < -0.05 ? <Badge type="amber">{delta.toFixed(2)}%</Badge>
                    : <Badge type="gray">Hold</Badge>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontSize: 11, color: T.text2, minWidth: 60 }}>
                    Prev: {prev ? prev.weight.toFixed(2) + "%" : "—"}
                  </div>
                  <div style={{ flex: 1 }}>
                    {s.isExit
                      ? <span style={{ fontSize: 13, color: T.red, fontWeight: 500 }}>Exiting</span>
                      : <input type="number" value={s.weight} min="0" max="100" step="0.1"
                          onChange={e => updateWeight(s.symbol, e.target.value)}
                          style={{ width: 80, padding: "5px 8px", border: `0.5px solid ${T.border2}`, borderRadius: 6, background: T.bg, color: T.text, fontSize: 14, textAlign: "right" }} />}
                  </div>
                  <button onClick={() => toggleExit(s.symbol)}
                    style={{ fontSize: 12, padding: "5px 10px", border: `0.5px solid ${T.border2}`, borderRadius: 6, cursor: "pointer", background: "transparent", color: s.isExit ? T.green : T.red }}>
                    {s.isExit ? "Undo" : "Exit"}
                  </button>
                  {!prev && <button onClick={() => removeStock(s.symbol)}
                    style={{ fontSize: 12, padding: "5px 8px", border: `0.5px solid ${T.border2}`, borderRadius: 6, cursor: "pointer", background: "transparent", color: T.text2 }}>✕</button>}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `0.5px solid ${T.border}`, display: "flex", flexDirection: isMobile ? "column" : "row", gap: 8 }}>
          <div style={{ flex: 2, position: "relative" }}>
            <input value={newSymbol} onChange={e => searchSymbols(e.target.value.toUpperCase())} placeholder="Search symbol (e.g. DIXON)"
              style={{ width: "100%", padding: "7px 10px", border: `0.5px solid ${T.border2}`, borderRadius: 8, background: T.bg, color: T.text, fontSize: 13, boxSizing: "border-box" }} />
            {searchResults.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, zIndex: 100, maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
                {searchResults.map(r => (
                  <div key={r.isin} onClick={() => selectSymbol(r)}
                    style={{ padding: "8px 12px", cursor: "pointer", borderBottom: `1px solid ${T.border}`, fontSize: 13 }}
                    onMouseEnter={e => e.currentTarget.style.background = T.blueBg}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <span style={{ fontWeight: 600 }}>{r.symbol}</span>
                    <span style={{ color: T.text2, marginLeft: 8, fontSize: 12 }}>{r.company}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <input type="number" value={newWeight} onChange={e => setNewWeight(e.target.value)} placeholder="Weight %"
            style={{ flex: 1, padding: "7px 10px", border: `0.5px solid ${T.border2}`, borderRadius: 8, background: T.bg, color: T.text, fontSize: 13 }} />
          <Btn onClick={addStock}>+ Add stock</Btn>
        </div>
      </Card>

      {preview && (
        <Card style={{ marginBottom: 16, border: `0.5px solid ${T.border2}` }}>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>Rebalance summary</div>
          {preview.exits.length > 0 && <div style={{ fontSize: 13, marginBottom: 8 }}><span style={{ color: T.red, fontWeight: 500 }}>Exits ({preview.exits.length}):</span> <span style={{ color: T.text2 }}>{preview.exits.join(", ")}</span></div>}
          {preview.entries.length > 0 && <div style={{ fontSize: 13, marginBottom: 8 }}><span style={{ color: T.green, fontWeight: 500 }}>New entries ({preview.entries.length}):</span> <span style={{ color: T.text2 }}>{preview.entries.join(", ")}</span></div>}
          {preview.changes.length > 0 && <div style={{ fontSize: 13, marginBottom: 8 }}><span style={{ fontWeight: 500 }}>Weight changes ({preview.changes.length}):</span> <span style={{ color: T.text2 }}>{preview.changes.map(c => `${c.symbol} ${c.from.toFixed(1)}%→${c.to.toFixed(1)}%`).join(", ")}</span></div>}
          <div style={{ fontSize: 13, marginTop: 8, color: totalWeight > 100.1 ? T.red : T.green, fontWeight: 500 }}>Total weight: {preview.total}% {totalWeight > 100.1 ? "⚠ Must be 100%" : "✓"}</div>
        </Card>
      )}

      <Card style={{ marginBottom: 16, background: paperMode ? "#FFFBEA" : T.bg }}>
        <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>{paperMode ? "🧪 Paper publish — basket generated but NOT sent to clients" : "⚡ Live publish — basket sent to ALL client accounts"}</div>
            <div style={{ fontSize: 12, color: T.text2 }}>{paperMode ? "Safe to test. No emails sent, no real orders placed on any client account." : "Clients will receive email + see basket on their portal immediately."}</div>
          </div>
          <button onClick={() => setPaperMode(!paperMode)}
            style={{ padding: "6px 14px", borderRadius: 8, border: `0.5px solid ${T.border2}`, background: paperMode ? "#F59E0B" : T.green, color: "white", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>
            {paperMode ? "Switch to Live" : "Switch to Paper"}
          </button>
        </div>
        {!paperMode && (
          <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 10, padding: "10px 12px", background: T.bg2, borderRadius: 8 }}>
            <div style={{ fontSize: 12, color: T.text2 }}>Max turnover cap (₹):</div>
            <input type="number" value={turnoverCap} onChange={e => setTurnoverCap(e.target.value)} placeholder="e.g. 200000 — leave blank for no limit"
              style={{ flex: 1, padding: "6px 10px", border: `0.5px solid ${T.border2}`, borderRadius: 6, background: T.bg, color: T.text, fontSize: 13 }} />
          </div>
        )}
      </Card>

      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 10, justifyContent: "flex-end" }}>
        <Btn onClick={buildPreview} style={{ width: isMobile ? "100%" : undefined }}>Preview changes</Btn>
        <Btn variant="primary" disabled={Math.abs(totalWeight - 100) > 0.5}
          style={{ background: paperMode ? "#F59E0B" : undefined, width: isMobile ? "100%" : undefined }}
          onClick={async () => {
            if (Math.abs(totalWeight - 100) > 0.5) { alert("Weights must sum to 100%"); return; }
            if (!paperMode && !window.confirm("Publish LIVE rebalance across all client accounts?")) return;
            try {
              const payload = {
                rebalance_date: new Date().toISOString().split("T")[0],
                is_paper_trade: paperMode,
                stocks: stocks.map(s => ({
                  symbol: s.symbol, isin: s.isin || "",
                  target_weight: s.isExit ? 0 : (parseFloat(s.weight) || 0),
                  action: s.isExit ? "exit" :
                    !prevPortfolio.find(p => p.symbol === s.symbol) ? "new" :
                    Math.abs((prevPortfolio.find(p => p.symbol === s.symbol)?.weight || 0) - (parseFloat(s.weight) || 0)) > 0.05 ?
                    ((parseFloat(s.weight) || 0) > (prevPortfolio.find(p => p.symbol === s.symbol)?.weight || 0) ? "buy" : "sell") : "hold"
                }))
              };
              const res = await apiFetch("/admin/rebalance/publish", { method: "POST", body: JSON.stringify(payload) }, token);
              if (res.success) setPublished(true);
              else alert("Publish failed: " + (res.message || "Unknown error"));
            } catch(err) { alert("Publish failed: " + err.message); }
          }}>
          {paperMode ? "🧪 Publish paper trade" : "⚡ Publish live rebalance →"}
        </Btn>
      </div>
    </div>
  );
}

// ─── CLIENT: EXECUTE REBALANCE ────────────────────────────────────────────────
function EditableAmount({ basket, token, portfolioId, onUpdated, onCancelled }) {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const totalTurnover = parseFloat(basket?.total_turnover || 0);

  const handleSave = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) { alert("Please enter a valid amount"); return; }
    setSaving(true);
    try {
      const res = await apiFetch(`/broker/basket/${basket.basket_id}/resize`,
        { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ amount: val }) }, token);
      if (res.success) {
        setEditing(false); setAmount("");
        // Fetch the updated basket from the backend and update state in place
        // so the page stays on the rebalance tab with new quantities visible
        const updated = await apiFetch(`/broker/basket/${portfolioId}`, {}, token);
        onUpdated(updated.basket || { ...basket, total_turnover: res.new_total });
      } else { alert("Error: " + (res.detail || "Unknown")); }
    } catch(e) { alert("Error: " + e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ marginBottom: 14 }}>
      {!editing ? (
        <div style={{ padding: "10px 12px", borderRadius: 8, background: T.bg2, fontSize: 12, color: T.text2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Est. investment: <b style={{ color: T.text }}>{fmtL(totalTurnover)}</b></span>
          <div style={{ display: "flex", gap: 12 }}>
            <span style={{ color: T.blue, cursor: "pointer", fontWeight: 500 }}
              onClick={() => { setEditing(true); setAmount(Math.round(totalTurnover).toString()); }}>
              ✏️ Edit amount
            </span>
            <span style={{ color: T.red, cursor: "pointer", fontWeight: 500 }}
              onClick={async () => {
                if (!window.confirm("Cancel this basket? Your advisor can then regenerate it with a corrected amount.")) return;
                try {
                  await apiFetch(`/admin/basket/${basket.basket_id}/cancel`, { method: "POST" }, token);
                  onCancelled();
                } catch(e) { alert("Could not cancel: " + e.message); }
              }}>
              ✕ Cancel
            </span>
          </div>
        </div>
      ) : (
        <div style={{ padding: "12px", borderRadius: 8, background: T.bg2, border: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 12, color: T.text2, marginBottom: 8 }}>
            Enter the amount you want to invest. All stock quantities will be recalculated proportionally.
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 13, color: T.text2 }}>₹</span>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
              placeholder="e.g. 300000"
              style={{ flex: 1, padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.border2}`,
                background: T.bg, color: T.text, fontSize: 14 }} />
            <Btn variant="primary" onClick={handleSave} disabled={saving} style={{ fontSize: 12, padding: "8px 14px" }}>
              {saving ? "Updating…" : "Update"}
            </Btn>
            <Btn onClick={() => { setEditing(false); setAmount(""); }} style={{ fontSize: 12, padding: "8px 14px" }}>
              Back
            </Btn>
          </div>
          {amount && parseFloat(amount) > 0 && (
            <div style={{ fontSize: 11, color: T.text2, marginTop: 8 }}>
              ₹{parseFloat(amount).toLocaleString("en-IN")} will be proportionally split across all {basket?.orders?.length || 21} stocks
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── SMART PORTFOLIO ANALYSIS FLOW ──────────────────────────────────────────
// Replaces the static basket display with a live Zerodha-aware comparison flow
function PortfolioAnalysisFlow({ token, portfolioId, isMobile }) {
  const [step, setStep] = useState("connect"); // connect | analyse | confirm | basket | done
  const [analysis, setAnalysis] = useState(null);
  const [pendingBasket, setPendingBasket] = useState(null);
  const [checkingBasket, setCheckingBasket] = useState(true);
  const [sellNonStrategy, setSellNonStrategy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check for existing pending basket first — if one exists show it directly
  useEffect(() => {
    if (!portfolioId) { setCheckingBasket(false); return; }
    apiFetch(`/broker/basket/${portfolioId}`, {}, token)
      .then(res => {
        if (res.basket && res.basket.orders?.length > 0) {
          setPendingBasket(res.basket);
          setStep("existing_basket");
        }
      })
      .catch(() => {})
      .finally(() => setCheckingBasket(false));
  }, [portfolioId, token]);

  // Check if Zerodha callback just happened (user returning from Zerodha)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requestToken = params.get("request_token");
    const status = params.get("status");
    if (requestToken && requestToken.length > 10 && status === "success") {
      window.history.replaceState({}, "", window.location.pathname);
      // Exchange token then auto-run analysis
      apiFetch(`/auth/zerodha/callback?request_token=${requestToken}`, {}, token)
        .then(() => runAnalysis(false))
        .catch(e => setError("Zerodha connection failed: " + e.message));
    }
  }, [token]);

  const connectZerodha = async () => {
    try {
      const res = await apiFetch("/auth/zerodha/login", {}, token);
      window.location.href = res.login_url;
    } catch(e) { setError(e.message); }
  };

  const runAnalysis = async (sellNS) => {
    setLoading(true); setError(null);
    try {
      const res = await apiFetch("/client/portfolio/analyse",
        { method: "POST", headers: {"Content-Type":"application/json"},
          body: JSON.stringify({ sell_non_strategy: sellNS ?? false }) }, token);
      setAnalysis(res);
      setSellNonStrategy(sellNS);
      setStep(res.non_strategy_holdings?.length > 0 && sellNS === null ? "confirm" : "basket");
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const executeBasket = () => {
    if (!analysis?.orders?.length) { alert("No orders to execute"); return; }
    const orders = analysis.orders.map(o => ({
      variety: "regular", tradingsymbol: o.symbol, exchange: "NSE",
      transaction_type: o.action, order_type: "MARKET", quantity: o.delta_qty, readonly: false
    }));
    const form = document.createElement("form");
    form.method = "POST";
    form.action = "https://kite.zerodha.com/connect/basket";
    const k = document.createElement("input"); k.type="hidden"; k.name="api_key"; k.value="yiw65fnp6s6ip23s";
    const d = document.createElement("input"); d.type="hidden"; d.name="data"; d.value=JSON.stringify(orders);
    form.appendChild(k); form.appendChild(d);
    document.body.appendChild(form);
    setTimeout(() => form.submit(), 100);
  };

  const fmtL = v => v >= 100000 ? `₹${(v/100000).toFixed(2)}L` : `₹${Math.round(v).toLocaleString("en-IN")}`;

  if (checkingBasket) return <Spinner />;

  // Show existing pending basket if one was found
  if (step === "existing_basket" && pendingBasket) {
    const buys = pendingBasket.orders.filter(o => o.action === "BUY");
    const sells = pendingBasket.orders.filter(o => o.action === "SELL");
    const totalTurnover = parseFloat(pendingBasket.total_turnover || 0);
    return (
      <div>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Pending rebalance</div>
            <div style={{ fontSize: 12, color: T.text2 }}>
              {sells.length} sells · {buys.length} buys · Est. {fmtL(totalTurnover)}
            </div>
          </div>

          {sells.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.red, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Sell</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {sells.map(o => (
                  <div key={o.symbol} style={{ padding: "6px 10px", borderRadius: 6, background: "#FEF2F2", border: "0.5px solid #FECACA", fontSize: 12 }}>
                    <span style={{ fontWeight: 600, color: T.red }}>{o.symbol}</span>
                    <span style={{ color: T.text2, marginLeft: 6 }}>{o.quantity} qty · {fmtL(o.estimated_value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {buys.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.green, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Buy</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {buys.map(o => (
                  <div key={o.symbol} style={{ padding: "6px 10px", borderRadius: 6, background: "#F0FDF4", border: "0.5px solid #BBF7D0", fontSize: 12 }}>
                    <span style={{ fontWeight: 600, color: T.green }}>{o.symbol}</span>
                    <span style={{ color: T.text2, marginLeft: 6 }}>{o.quantity} qty · {fmtL(o.estimated_value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ fontSize: 12, color: T.text2, marginBottom: 14, lineHeight: 1.6 }}>
            Zerodha will show you the full basket to review before placing any orders.
          </div>
          <Btn variant="primary" style={{ width: "100%", padding: 14, fontSize: 14, background: "#387ED1" }}
            onClick={() => {
              const orders = pendingBasket.orders.map(o => ({
                variety: "regular", tradingsymbol: o.symbol, exchange: "NSE",
                transaction_type: o.action, order_type: "MARKET", quantity: o.quantity, readonly: false
              }));
              const form = document.createElement("form");
              form.method = "POST"; form.action = "https://kite.zerodha.com/connect/basket";
              const k = document.createElement("input"); k.type="hidden"; k.name="api_key"; k.value="yiw65fnp6s6ip23s";
              const d = document.createElement("input"); d.type="hidden"; d.name="data"; d.value=JSON.stringify(orders);
              form.appendChild(k); form.appendChild(d);
              document.body.appendChild(form);
              setTimeout(() => form.submit(), 100);
            }}>
            ⚡ Execute on Zerodha →
          </Btn>
        </Card>
      </div>
    );
  }

  // Step 1: Connect Zerodha
  if (step === "connect") return (
    <Card>
      <div style={{ textAlign: "center", padding: "20px 0" }}>
        <div style={{ fontSize: 28, marginBottom: 12 }}>🔗</div>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Connect your Zerodha account</div>
        <div style={{ fontSize: 13, color: T.text2, marginBottom: 24, lineHeight: 1.6 }}>
          ThinkWealth will pull your current holdings, compare them against the latest model portfolio,
          and show you exactly what needs to change — with your position sizing.
        </div>
        {error && <div style={{ color: T.red, fontSize: 12, marginBottom: 16 }}>{error}</div>}
        <Btn variant="primary" style={{ width: "100%", padding: 14, fontSize: 14, background: "#387ED1" }}
          onClick={connectZerodha}>
          Connect Zerodha to analyse portfolio →
        </Btn>
      </div>
    </Card>
  );

  if (loading) return <Spinner />;

  // Step 2: Non-strategy stocks question
  if (step === "confirm" && analysis?.non_strategy_holdings?.length > 0) return (
    <Card>
      <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>You hold stocks outside this strategy</div>
      <div style={{ fontSize: 12, color: T.text2, marginBottom: 16, lineHeight: 1.6 }}>
        These stocks are not part of the ThinkWealth momentum portfolio. What would you like to do with them?
      </div>
      <div style={{ marginBottom: 16 }}>
        {analysis.non_strategy_holdings.map(h => (
          <div key={h.symbol} style={{ display: "flex", justifyContent: "space-between",
            padding: "8px 12px", borderRadius: 8, background: T.bg2, marginBottom: 6, fontSize: 13 }}>
            <span style={{ fontWeight: 500 }}>{h.symbol}</span>
            <span style={{ color: T.text2 }}>{h.quantity} qty · {fmtL(h.market_value)}</span>
          </div>
        ))}
      </div>
      <div style={{ padding: "10px 12px", borderRadius: 8, background: T.amberBg || "#FEF3C7",
        fontSize: 12, color: T.text2, marginBottom: 20 }}>
        Total value: <b>{fmtL(analysis.summary.non_strategy_stocks_value)}</b>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Btn variant="primary" onClick={() => { setSellNonStrategy(false); setStep("basket"); runAnalysis(false); }}>
          Keep them — only rebalance strategy stocks
        </Btn>
        <Btn onClick={() => { setSellNonStrategy(true); setStep("basket"); runAnalysis(true); }}
          style={{ background: T.red, color: "#fff" }}>
          Sell them and deploy proceeds into strategy
        </Btn>
      </div>
    </Card>
  );

  // Step 3: Basket summary
  if (step === "basket" && analysis) {
    const buys = analysis.orders.filter(o => o.action === "BUY");
    const sells = analysis.orders.filter(o => o.action === "SELL");
    const s = analysis.summary;
    return (
      <div>
        {/* Summary cards */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
          {[
            { label: "Strategy holdings", value: fmtL(s.strategy_stocks_value) },
            { label: "Available cash", value: fmtL(s.available_cash) },
            { label: "Non-strategy stocks", value: fmtL(s.non_strategy_stocks_value) },
            { label: "Total investable", value: fmtL(s.investable_amount) },
          ].map(({ label, value }) => (
            <div key={label} style={{ padding: "12px", borderRadius: 10, background: T.bg2,
              border: `0.5px solid ${T.border}` }}>
              <div style={{ fontSize: 11, color: T.text2, marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{value}</div>
            </div>
          ))}
        </div>

        {/* Orders */}
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>
              {analysis.is_fresh_client ? "Initial portfolio" : "Rebalance changes"}
            </div>
            <div style={{ fontSize: 12, color: T.text2 }}>
              {sells.length} sells · {buys.length} buys · Est. {fmtL(analysis.total_turnover)}
            </div>
          </div>

          {analysis.orders.length === 0 && (
            <div style={{ textAlign: "center", padding: "20px 0", color: T.text2, fontSize: 13 }}>
              ✓ Your portfolio is already aligned to the model. No changes needed.
            </div>
          )}

          {sells.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.red, fontWeight: 600, textTransform: "uppercase",
                letterSpacing: 1, marginBottom: 8 }}>Sell</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {sells.map(o => (
                  <div key={o.symbol} style={{ padding: "6px 10px", borderRadius: 6,
                    background: "#FEF2F2", border: "0.5px solid #FECACA", fontSize: 12 }}>
                    <span style={{ fontWeight: 600, color: T.red }}>{o.symbol}</span>
                    <span style={{ color: T.text2, marginLeft: 6 }}>{o.delta_qty} qty</span>
                    <span style={{ color: T.text2, marginLeft: 4 }}>· {fmtL(o.estimated_value)}</span>
                    <span style={{ color: T.text2, marginLeft: 4, fontSize: 10 }}>({o.reason})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {buys.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.green, fontWeight: 600, textTransform: "uppercase",
                letterSpacing: 1, marginBottom: 8 }}>Buy</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {buys.map(o => (
                  <div key={o.symbol} style={{ padding: "6px 10px", borderRadius: 6,
                    background: "#F0FDF4", border: "0.5px solid #BBF7D0", fontSize: 12 }}>
                    <span style={{ fontWeight: 600, color: T.green }}>{o.symbol}</span>
                    <span style={{ color: T.text2, marginLeft: 6 }}>{o.delta_qty} qty</span>
                    <span style={{ color: T.text2, marginLeft: 4 }}>· {fmtL(o.estimated_value)}</span>
                    <span style={{ color: T.text2, marginLeft: 4, fontSize: 10 }}>({o.reason})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {analysis.orders.length > 0 && (
            <>
              <div style={{ fontSize: 12, color: T.text2, marginBottom: 14, lineHeight: 1.6 }}>
                Zerodha will show you the full basket to review before placing any orders.
              </div>
              <Btn variant="primary" style={{ width: "100%", padding: 14, fontSize: 14, background: "#387ED1" }}
                onClick={executeBasket}>
                ⚡ Execute on Zerodha →
              </Btn>
            </>
          )}

          <div style={{ marginTop: 12, textAlign: "center" }}>
            <span style={{ fontSize: 12, color: T.text2, cursor: "pointer" }}
              onClick={() => { setStep("connect"); setAnalysis(null); }}>
              ← Re-analyse with fresh Zerodha data
            </span>
          </div>
        </Card>
      </div>
    );
  }

  return <Spinner />;
}

function RebalancePage({ token, portfolioId, auth, isMobile }) {
  const [basket, setBasket] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);
  const [results, setResults] = useState(null);
  const [capValue, setCapValue] = useState("");
  const [testMode, setTestMode] = useState(false);
  const [openSection, setOpenSection] = useState("basket");

  useEffect(() => {
    if (!portfolioId) return;
    Promise.all([
      apiFetch(`/portfolio/${portfolioId}/holdings/live`, {}, token),
      apiFetch(`/broker/basket/${portfolioId}`, {}, token),
    ]).then(([h, b]) => { setHoldings(h.holdings || []); setBasket(b.basket || null); })
      .catch(console.error).finally(() => setLoading(false));

    // Clear any stale URL params immediately to prevent re-triggering
    const params = new URLSearchParams(window.location.search);
    const requestToken = params.get("request_token");
    const status = params.get("status");
    // Only process if we have BOTH a valid request_token AND status=success
    // AND the token looks like a real Zerodha token (non-empty, min length)
    if (requestToken && requestToken.length > 10 && status === "success") {
      window.history.replaceState({}, "", window.location.pathname);
      apiFetch(`/auth/zerodha/callback?request_token=${requestToken}`, {}, token)
        .then(() => {
          // Now mark the basket as executed via the mark-executed endpoint
          return apiFetch(`/broker/basket/${portfolioId}`, {}, token);
        })
        .then(res => {
          if (res.basket) {
            return apiFetch(`/admin/basket/${res.basket.basket_id}/mark-executed`, { method: "POST" }, token);
          }
        })
        .then(() => {
          setDone(true);
          setResults([{ status: "placed", message: "Orders executed on Zerodha successfully" }]);
        })
        .catch(e => {
          // Only show error - don't fake success if callback failed
          alert("Order confirmation failed: " + e.message + ". Please check your Zerodha order book.");
        });
    }
  }, [token, portfolioId]);



  if (done) return (
    <div style={{ padding: "20px 0" }}>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ fontSize: 36, marginBottom: 12, color: T.green }}>✓</div>
        <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 4 }}>Rebalance executed</div>
        <div style={{ fontSize: 13, color: T.text2 }}>Orders placed on Zerodha. Check your order book for fill status.</div>
      </div>
      <Card>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>Order summary</div>
        {(results || []).map((r, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 0", borderBottom: `0.5px solid ${T.border}` }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{r.symbol}</div>
              <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>{r.order_id || r.reason || r.error || "—"}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, color: r.action === "BUY" ? T.green : T.red, fontWeight: 600, marginBottom: 4 }}>{r.action} · {r.quantity} qty</div>
              <Badge type={r.status === "placed" ? "green" : r.status === "skipped" ? "amber" : "red"}>{r.status}</Badge>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );

  if (loading) return <Spinner />;

  const totalValue = holdings.reduce((s, h) => s + (h.market_value || 0), 0);
  const sells = basket?.orders?.filter(o => o.action === "SELL") || [];
  const buys = basket?.orders?.filter(o => o.action === "BUY") || [];
  const totalTurnover = parseFloat(basket?.total_turnover || 0);

  const toggleSection = (id) => setOpenSection(s => s === id ? null : id);

  // Minimal SVG icons
  const Icon = ({ name, size = 15, color = T.text2 }) => {
    const paths = {
      basket:   "M3 3h2l.4 2M7 13a2 2 0 1 0 4 0 2 2 0 0 0-4 0zm8 0a2 2 0 1 0 4 0 2 2 0 0 0-4 0zM5.4 5h13l-1.68 8.4A2 2 0 0 1 14.8 15H7.2a2 2 0 0 1-1.96-1.6L3.88 5.6",
      holdings: "M3 3v18h18M7 16l4-4 4 4 4-6",
      lumpsum:  "M12 5v14M5 12h14",
      chevron:  "M6 9l6 6 6-6",
    };
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d={paths[name]} />
      </svg>
    );
  };

  const SectionHeader = ({ id, icon, title, subtitle, accent }) => {
    const isOpen = openSection === id;
    return (
      <div onClick={() => toggleSection(id)} style={{
        display: "flex", alignItems: "center", gap: 12, padding: "14px 16px",
        cursor: "pointer", userSelect: "none",
        borderBottom: isOpen ? `0.5px solid ${T.border}` : "none",
      }}>
        <div style={{
          width: 34, height: 34, borderRadius: 10,
          background: accent + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <Icon name={icon} color={accent} size={16} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: T.text3, marginTop: 1 }}>{subtitle}</div>}
        </div>
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
    );
  };

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: T.text }}>
          {auth.role === "admin" ? "My rebalance basket" : "Execute rebalance"}
        </div>
        <div style={{ fontSize: 12, color: T.text2, marginTop: 3 }}>
          Portfolio value: {fmtL(totalValue)} · {holdings.length} stocks
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

        {/* ── Section 1: Smart Portfolio Analysis (replaces old static basket) ── */}
        <PortfolioAnalysisFlow token={token} portfolioId={portfolioId} isMobile={isMobile} />

        {/* ── HIDDEN: old basket section kept for reference but not rendered ── */}
        {false && (
          <Card style={{ padding: 0, overflow: "hidden" }}>
          <SectionHeader
            id="basket" icon="basket" title="Pending rebalance"
            subtitle={basket ? `${basket.rebalance_date} · ${sells.length} sells · ${buys.length} buys` : "No pending rebalance"}
            accent={T.blue}
          />
          {openSection === "basket" && (
            <div style={{ padding: 16 }}>
              {!basket ? (
                <div>
                  <div style={{ padding: "20px", borderRadius: 10, background: T.bg2, textAlign: "center" }}>
                    <div style={{ fontSize: 28, marginBottom: 10 }}>✓</div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: T.text, marginBottom: 6 }}>You are all caught up</div>
                    <div style={{ fontSize: 12, color: T.text2, lineHeight: 1.7 }}>
                      No pending orders right now. Your advisor will notify you when the next rebalance is ready.
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* SELL pills */}
                  {sells.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, color: T.text3, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 8 }}>Sell</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {sells.map((o, i) => (
                          <div key={i} style={{ padding: "6px 11px", borderRadius: 8, background: T.redBg, fontSize: 12 }}>
                            <span style={{ color: T.red, fontWeight: 600 }}>{o.symbol}</span>
                            <span style={{ color: T.text2, marginLeft: 6 }}>{o.quantity} qty</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* BUY pills */}
                  {buys.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 10, color: T.text3, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 8 }}>Buy</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {buys.map((o, i) => (
                          <div key={i} style={{ padding: "6px 11px", borderRadius: 8, background: T.greenBg, fontSize: 12 }}>
                            <span style={{ color: T.green, fontWeight: 600 }}>{o.symbol}</span>
                            <span style={{ color: T.text2, marginLeft: 6 }}>{o.quantity} qty</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Est. investment + Edit amount inline */}
                  <EditableAmount basket={basket} token={token} portfolioId={portfolioId} onUpdated={(newBasket) => setBasket(newBasket)} onCancelled={() => setBasket(null)} />

                  {/* Test mode toggle */}
                  <div style={{ borderTop: `0.5px solid ${T.border}`, paddingTop: 14, marginBottom: 14 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                      <input type="checkbox" checked={testMode} onChange={e => setTestMode(e.target.checked)} />
                      Test mode (cap order value)
                    </label>
                    {testMode && (
                      <input type="number" placeholder="Max ₹ to deploy" value={capValue} onChange={e => setCapValue(e.target.value)}
                        style={{ width: "100%", marginTop: 10, padding: "9px 12px", border: `0.5px solid ${T.border2}`, borderRadius: 8, background: T.bg, color: T.text, fontSize: 16, boxSizing: "border-box" }} />
                    )}
                  </div>

                  {/* Execute via Kite Publisher Offsite Basket */}
                  <div style={{ marginTop: 4 }}>
                    <div style={{ fontSize: 12, color: T.text2, marginBottom: 12, lineHeight: 1.6 }}>
                      Click below to execute all orders on Zerodha. You will be taken to Zerodha to review and confirm the basket — then redirected back here.
                    </div>
                    <Btn variant="primary" style={{ width: "100%", padding: "13px", fontSize: 14, background: "#387ED1" }}
                      onClick={() => {
                        if (!basket || !basket.orders || basket.orders.length === 0) {
                          alert("No orders in basket"); return;
                        }
                        const orders = basket.orders.map(o => ({
                          variety: "regular",
                          tradingsymbol: o.symbol,
                          exchange: "NSE",
                          transaction_type: o.action,
                          order_type: "MARKET",
                          quantity: o.quantity,
                          readonly: false
                        }));
                        // Use a hidden form that targets _self so the redirect
                        // comes back to this page with request_token in URL
                        const form = document.createElement("form");
                        form.method = "POST";
                        form.action = "https://kite.zerodha.com/connect/basket";
                        form.target = "_self";
                        const apiKeyInput = document.createElement("input");
                        apiKeyInput.type = "hidden";
                        apiKeyInput.name = "api_key";
                        apiKeyInput.value = "yiw65fnp6s6ip23s";
                        const dataInput = document.createElement("input");
                        dataInput.type = "hidden";
                        dataInput.name = "data";
                        dataInput.value = JSON.stringify(orders);
                        form.appendChild(apiKeyInput);
                        form.appendChild(dataInput);
                        document.body.appendChild(form);
                        // Small delay to ensure DOM is ready
                        setTimeout(() => form.submit(), 100);
                      }}>
                      ⚡ Execute on Zerodha →
                    </Btn>
                    <div style={{ textAlign: "center", marginTop: 10, fontSize: 11, color: T.text2 }}>
                      Zerodha will show you the full basket to review before placing any orders
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </Card>
        )}

        {/* ── Section 2: Current holdings ── */}
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <SectionHeader
            id="holdings" icon="holdings" title="Current holdings"
            subtitle={`${holdings.length} stocks · ${fmtL(totalValue)}`}
            accent={T.green}
          />
          {openSection === "holdings" && (
            <div style={{ padding: "0 16px 8px" }}>
              {holdings.length === 0
                ? <div style={{ fontSize: 13, color: T.text2, padding: "14px 0" }}>No holdings found.</div>
                : holdings.map((h, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: `0.5px solid ${T.border}` }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{h.symbol}</div>
                      <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>{h.quantity} qty · avg ₹{fmt(h.avg_cost)}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>₹{fmt(h.market_value)}</div>
                      <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>{h.weight_pct?.toFixed(2)}%</div>
                    </div>
                  </div>
                ))
              }
            </div>
          )}
        </Card>

        {/* ── Section 3: Add lumpsum ── */}
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <SectionHeader
            id="lumpsum" icon="lumpsum" title="Add lumpsum"
            subtitle="Deploy funds proportionally across holdings"
            accent={T.amber}
          />
          {openSection === "lumpsum" && (
            <div style={{ padding: 16 }}>
              <input type="number" placeholder="Amount (₹)" id="ls-amt"
                style={{ width: "100%", padding: "10px 12px", border: `0.5px solid ${T.border2}`, borderRadius: 8, background: T.bg, color: T.text, fontSize: 16, boxSizing: "border-box", marginBottom: 12 }} />
              <Btn variant="primary" style={{ width: "100%", padding: "13px" }} onClick={async () => {
                const amt = document.getElementById("ls-amt").value;
                if (!amt || parseFloat(amt) <= 0) return;
                try {
                  await apiFetch("/portfolio/add-funds", {
                    method: "POST",
                    body: JSON.stringify({ portfolio_id: portfolioId, amount: parseFloat(amt), flow_type: "lumpsum" })
                  }, token);
                  alert(`₹${parseInt(amt).toLocaleString("en-IN")} lumpsum recorded. Funds will be deployed in the next rebalance.`);
                  document.getElementById("ls-amt").value = "";
                } catch(e) { alert("Failed: " + e.message); }
              }}>Add funds</Btn>
            </div>
          )}
        </Card>

      </div>
    </div>
  );
}

// ─── SIP PAGE ────────────────────────────────────────────────────────────────
function SIPPage({ token }) {
  const [type, setType] = useState("sip");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [history, setHistory] = useState([]);
  const [portfolioId, setPortfolioId] = useState(null);

  useEffect(() => {
    // Fetch client's portfolio id and cash flow history
    apiFetch("/client/portfolios/live", {}, token)
      .then(d => {
        const port = d.portfolios?.[0];
        if (port) {
          setPortfolioId(port.id);
          return apiFetch(`/portfolio/${port.id}/nav/live`, {}, token);
        }
      })
      .then(d => { if (d?.cash_flows) setHistory(d.cash_flows); })
      .catch(console.error);
  }, [token]);

  const handle = async () => {
    if (!amount || parseFloat(amount) <= 0) { alert("Please enter a valid amount"); return; }
    setSaving(true);
    try {
      await apiFetch("/portfolio/add-funds", {
        method: "POST",
        body: JSON.stringify({
          portfolio_id: portfolioId || "",
          amount: parseFloat(amount),
          flow_type: type,
          notes: notes || null,
        })
      }, token);
      setSaved({ amount: parseFloat(amount), type });
      setAmount(""); setNotes("");
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  if (saved) return (
    <div style={{ textAlign: "center", padding: "48px 20px" }}>
      <div style={{ fontSize: 40, marginBottom: 16, color: T.green }}>✓</div>
      <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
        ₹{fmt(saved.amount)} recorded
      </div>
      <div style={{ fontSize: 13, color: T.text2, marginBottom: 24, lineHeight: 1.7 }}>
        Your {saved.type === "sip" ? "SIP" : "lumpsum"} has been logged.<br/>
        The funds will be deployed in the next rebalance.
      </div>
      <Btn variant="primary" onClick={() => setSaved(null)}>Add another</Btn>
    </div>
  );

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Add funds</div>

        {/* Type toggle */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[["sip", "Monthly SIP"], ["lumpsum", "Lumpsum"], ["withdrawal", "Withdrawal"]].map(([val, label]) => (
            <button key={val} onClick={() => setType(val)}
              style={{ flex: 1, padding: "8px 4px", borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: "pointer",
                border: `0.5px solid ${type === val ? T.text : T.border}`,
                background: type === val ? T.text : "transparent",
                color: type === val ? T.bg : T.text2 }}>
              {label}
            </button>
          ))}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: T.text2, display: "block", marginBottom: 6 }}>
            Amount (₹) {type === "withdrawal" && <span style={{ color: T.red }}>— funds will be deducted</span>}
          </label>
          <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
            placeholder="e.g. 25000"
            style={{ width: "100%", padding: "10px 12px", border: `0.5px solid ${T.border2}`,
              borderRadius: 8, background: T.bg, color: T.text, fontSize: 16, boxSizing: "border-box" }} />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, color: T.text2, display: "block", marginBottom: 6 }}>Notes (optional)</label>
          <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="e.g. May SIP installment"
            style={{ width: "100%", padding: "10px 12px", border: `0.5px solid ${T.border2}`,
              borderRadius: 8, background: T.bg, color: T.text, fontSize: 14, boxSizing: "border-box" }} />
        </div>

        <Btn variant="primary" style={{ width: "100%", padding: "13px" }}
          onClick={handle} disabled={saving || !amount}>
          {saving ? "Recording…" : type === "withdrawal" ? "Record withdrawal" : "Add funds"}
        </Btn>

        {type === "sip" && (
          <div style={{ marginTop: 12, padding: "10px 12px", background: T.blueBg, borderRadius: 8, fontSize: 12, color: T.blueText, lineHeight: 1.6 }}>
            SIP funds are logged here and deployed in your next rebalance. Ensure funds are available in your broker account before execution.
          </div>
        )}
      </Card>

      {/* Cash flow history */}
      {history.length > 0 && (
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Recent transactions</div>
          {history.slice(0, 8).map((cf, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "10px 0", borderBottom: `0.5px solid ${T.border}` }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: cf.type === "withdrawal" ? T.red : T.green }}>
                  {cf.type === "withdrawal" ? "−" : "+"}₹{fmt(cf.amount)}
                </div>
                <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>{cf.date}</div>
              </div>
              <Badge type={cf.type === "sip" ? "blue" : cf.type === "lumpsum" ? "amber" : cf.type === "withdrawal" ? "red" : "gray"}>
                {cf.type}
              </Badge>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ─── NAV ITEMS ───────────────────────────────────────────────────────────────
// ─── HEDGE INTELLIGENCE PAGE (ADMIN ONLY) ────────────────────────────────────

const MACRO_SIGNALS_DEFAULT = [
  { key: "india_vix",       label: "India VIX",      value: 15.4, min: 8,   max: 40,  step: 0.5,  weight: 0.25 },
  { key: "iv_rank",         label: "IV Rank (%)",    value: 52,   min: 0,   max: 100, step: 1,    weight: 0.20 },
  { key: "put_call_ratio",  label: "Put-Call Ratio", value: 0.88, min: 0.4, max: 2.0, step: 0.05, weight: 0.15 },
  { key: "nifty_rsi",       label: "Nifty RSI (14d)",value: 56,   min: 20,  max: 80,  step: 0.5,  weight: 0.12 },
  { key: "fii_flow",        label: "FII Flow",       value: 0.6,  min: -1,  max: 1,   step: 0.1,  weight: 0.10 },
  { key: "fed_surprise",    label: "Fed Surprise",   value: 0.7,  min: -1,  max: 1,   step: 0.1,  weight: 0.08 },
  { key: "inr_usd",         label: "INR/USD",        value: 0.4,  min: -1,  max: 1,   step: 0.1,  weight: 0.06 },
  { key: "brent_crude",     label: "Brent Crude",    value: 0.3,  min: -1,  max: 1,   step: 0.1,  weight: 0.04 },
];

function scoreMacroSignal(key, value) {
  if (key === "india_vix") {
    if (value >= 13 && value <= 18) return 1.0;
    if (value > 18 && value <= 22)  return 0.3;
    if (value > 22)                 return -0.5;
    return -0.3;
  }
  if (key === "iv_rank") {
    if (value >= 60) return 1.0;
    if (value >= 45) return 0.7;
    if (value >= 30) return 0.2;
    return -0.3;
  }
  if (key === "put_call_ratio") {
    if (value >= 0.7 && value <= 1.1) return 1.0;
    if (value > 1.1 && value <= 1.3)  return 0.2;
    if (value > 1.3)                  return -1.0;
    return -0.2;
  }
  if (key === "nifty_rsi") {
    if (value >= 38 && value <= 65) return 1.0;
    if (value > 65 && value <= 75)  return -0.2;
    if (value > 75)                 return -0.8;
    return -0.6;
  }
  return Math.max(-1, Math.min(1, value));
}

function computePutSellScore(signals) {
  const vix = signals.find(s => s.key === "india_vix")?.value || 15;
  const pcr = signals.find(s => s.key === "put_call_ratio")?.value || 1.0;
  const raw = signals.reduce((acc, s) => acc + scoreMacroSignal(s.key, s.value) * s.weight, 0);
  if (vix > 20 || pcr > 1.3) return Math.min(raw, 0.3);
  return raw;
}

function computeCallSellScore(signals) {
  const vix = signals.find(s => s.key === "india_vix")?.value || 15;
  const rsi = signals.find(s => s.key === "nifty_rsi")?.value || 50;
  const fii = signals.find(s => s.key === "fii_flow")?.value || 0;
  let score = signals.reduce((acc, s) => acc + scoreMacroSignal(s.key, s.value) * s.weight, 0) * 0.7;
  if (rsi > 70) score -= 0.3;
  if (fii > 0.7) score -= 0.2;
  if (vix > 28) score -= 0.4;
  return Math.max(-1, Math.min(1, score));
}

function getMacroLabel(score) {
  if (score > 0.55)  return { label: "Strongly Bullish",  type: "green" };
  if (score > 0.20)  return { label: "Mildly Bullish",    type: "green" };
  if (score > -0.10) return { label: "Neutral",           type: "gray"  };
  if (score > -0.40) return { label: "Mildly Bearish",    type: "amber" };
  return               { label: "Strongly Bearish",       type: "red"   };
}

const TECH_ZONES = [
  { key: "bullish",          label: "Bullish",           score: 0.8,  desc: "Price above Supertrend. EMA20 sloping up.",             putSell: "Favored — market moves away from put strike",    callSell: "Caution — rally may continue" },
  { key: "bullish_pullback", label: "Bullish Pullback",  score: 0.4,  desc: "Above Supertrend but pulled back below EMA20.",         putSell: "Moderate — trend intact but soft near-term",    callSell: "Moderate — correction limits upside risk" },
  { key: "neutral",          label: "Neutral",           score: 0.0,  desc: "Price around EMA20. Supertrend flat. No clear signal.", putSell: "Standard if macro supports",                    callSell: "Standard deployment" },
  { key: "bearish_pullback", label: "Bearish Pullback",  score: -0.4, desc: "Below Supertrend but bounced above EMA20.",             putSell: "Avoid — downtrend may resume toward put strike", callSell: "Favored — bounce likely limited" },
  { key: "bearish",          label: "Bearish",           score: -0.8, desc: "Price below Supertrend. EMA20 sloping down.",           putSell: "Strongly avoid — long put working",              callSell: "Can deploy — nobody expects 25% rally" },
];

const NIFTY_SPOT = 24000;
const LOT_SIZE   = 75;

function getLotCount(score, maxLots, threshold) {
  if (score < threshold) return 0;
  if (score > 0.80) return maxLots;
  if (score > 0.60) return Math.max(1, Math.round(maxLots * 0.75));
  if (score > 0.40) return Math.max(1, Math.round(maxLots * 0.50));
  return Math.max(1, Math.round(maxLots * 0.25));
}

function HedgeScoreBar({ score, threshold, color }) {
  const pct  = ((score + 1) / 2) * 100;
  const tpct = ((threshold + 1) / 2) * 100;
  return (
    <div style={{ position: "relative", height: 6, background: T.bg3, borderRadius: 3, overflow: "visible" }}>
      <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${Math.max(0, Math.min(100, pct))}%`, background: color, borderRadius: 3, transition: "width .4s ease" }} />
      <div style={{ position: "absolute", left: `${tpct}%`, top: -2, height: 10, width: 2, background: T.text3, borderRadius: 1 }} title="threshold" />
    </div>
  );
}

function HedgeIntelligencePage({ token, isMobile }) {
  const [tab, setTab]             = useState("overview");
  const [signals, setSignals]     = useState(MACRO_SIGNALS_DEFAULT);
  const [techZone, setTechZone]   = useState("bullish_pullback");
  const [clients, setClients]     = useState([]);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [fetchError, setFetchError]   = useState(false);
  const [fetchedAt, setFetchedAt]     = useState(null);
  const [fedDetail, setFedDetail]     = useState(null);

  // Auto-fetch all 8 live market signals on mount — all fully automatic
  useEffect(() => {
    apiFetch("/hedge/market-data", {}, token)
      .then(data => {
        setSignals(prev => prev.map(s => ({
          ...s,
          value:       data[s.key] !== undefined ? data[s.key] : s.value,
          autoFetched: data.auto_fetched?.includes(s.key) ?? false,
          manualOnly:  false, // nothing manual anymore
        })));
        setFetchedAt(data.fetched_at || null);
        setFedDetail(data.fed_detail || null);
        setFetchError(false);
      })
      .catch(() => setFetchError(true))
      .finally(() => setDataLoading(false));
  }, [token]);

  // Load clients for beta panel
  useEffect(() => {
    apiFetch("/admin/dashboard/live", {}, token)
      .then(d => setClients(d.clients || []))
      .catch(() => {});
  }, [token]);

  const macroScore   = signals.reduce((acc, s) => acc + scoreMacroSignal(s.key, s.value) * s.weight, 0);
  const putScore     = computePutSellScore(signals);
  const callScore    = computeCallSellScore(signals);
  const techScore    = TECH_ZONES.find(z => z.key === techZone)?.score ?? 0;
  const combined     = macroScore * 0.6 + techScore * 0.4;
  const macroLabel   = getMacroLabel(macroScore);
  const techLabel    = TECH_ZONES.find(z => z.key === techZone);
  const combinedLabel= getMacroLabel(combined);

  const putDeployed  = putScore  >= 0.40;
  const callDeployed = callScore >= 0.25;

  const labelColor = (type) => ({ green: T.green, amber: T.amber, red: T.red, gray: T.text2 })[type] || T.text2;
  const labelBg    = (type) => ({ green: T.greenBg, amber: T.amberBg, red: T.redBg, gray: T.bg2 })[type] || T.bg2;
  const labelFg    = (type) => ({ green: T.greenText, amber: T.amberText, red: T.redText, gray: T.text2 })[type] || T.text2;

  const updateSignal = (key, val) =>
    setSignals(prev => prev.map(s => s.key === key ? { ...s, value: parseFloat(val) } : s));

  const saveSignals = async () => {
    setSaving(true);
    try {
      await apiFetch("/hedge/signal-score", {
        method: "POST",
        body: JSON.stringify({
          signals: Object.fromEntries(signals.map(s => [s.key, s.value])),
          tech_zone: techZone,
          macro_score: macroScore,
          put_sell_score: putScore,
          call_sell_score: callScore,
          combined_score: combined,
        }),
      }, token);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { alert("Save failed: " + e.message); }
    finally { setSaving(false); }
  };

  const TABS = [
    { id: "overview", label: "Overview"             },
    { id: "macro",    label: "Macro signals"        },
    { id: "tech",     label: "Tech zone"            },
    { id: "clients",  label: "Client positions"     },
  ];

  // ── Score pill ──
  const ScorePill = ({ label, type }) => (
    <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
      background: labelBg(type), color: labelFg(type) }}>
      {label}
    </span>
  );

  // ── Section header ──
  const SectionHeader = ({ children }) => (
    <div style={{ fontSize: 11, fontWeight: 500, color: T.text3, textTransform: "uppercase",
      letterSpacing: ".06em", marginBottom: 12, marginTop: 4 }}>
      {children}
    </div>
  );

  return (
    <div>
      {/* Page title — desktop only (mobile uses the standard header) */}
      {!isMobile && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 500 }}>Hedge Intelligence</div>
              <div style={{ fontSize: 12, color: T.text2, marginTop: 2 }}>
                Admin only · Macro + technical scoring · Per-client option recommendations
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <ScorePill label={combinedLabel.label} type={combinedLabel.type} />
              <Btn onClick={saveSignals} disabled={saving} style={{ fontSize: 12 }}>
                {saving ? "Saving…" : saved ? "✓ Saved" : "Save signals"}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {isMobile && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <ScorePill label={combinedLabel.label} type={combinedLabel.type} />
          <Btn onClick={saveSignals} disabled={saving} style={{ fontSize: 11 }}>
            {saving ? "Saving…" : saved ? "✓ Saved" : "Save"}
          </Btn>
        </div>
      )}

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 2, marginBottom: 20, background: T.bg2,
        borderRadius: 8, padding: 3, border: `0.5px solid ${T.border}` }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1, padding: "7px 4px", borderRadius: 6, fontSize: isMobile ? 11 : 12,
            fontWeight: 500, cursor: "pointer", border: "none", transition: "all .15s",
            background: tab === t.id ? T.bg : "transparent",
            color: tab === t.id ? T.text : T.text2,
            boxShadow: tab === t.id ? `0 1px 3px rgba(0,0,0,0.08)` : "none",
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {tab === "overview" && (
        <div>
          {/* Three score cards */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3,1fr)", gap: 12, marginBottom: 16 }}>
            {dataLoading && (
              <div style={{ gridColumn: "1/-1", fontSize: 11, color: T.text3, padding: "6px 10px",
                background: T.bg2, borderRadius: 6, marginBottom: 4 }}>
                Fetching live market signals…
              </div>
            )}
            {!dataLoading && !fetchError && (
              <div style={{ gridColumn: "1/-1", fontSize: 11, color: T.green, padding: "5px 10px",
                background: T.greenBg, borderRadius: 6, marginBottom: 4,
                display: "flex", justifyContent: "space-between" }}>
                <span>✓ Signals auto-fetched — India VIX · PCR · RSI · FII · INR · Brent</span>
                {fetchedAt && <span style={{ color: T.greenText }}>as of {fetchedAt}</span>}
              </div>
            )}
            {!dataLoading && fetchError && (
              <div style={{ gridColumn: "1/-1", fontSize: 11, color: T.amber, padding: "5px 10px",
                background: T.amberBg, borderRadius: 6, marginBottom: 4 }}>
                ⚠ Could not reach market data endpoint — showing defaults. Go to Macro Signals tab to adjust manually.
              </div>
            )}
            {[
              { title: "Macro score",    score: macroScore, lbl: macroLabel,    sub: "8 signals · updated weekly" },
              { title: "Tech score",     score: techScore,  lbl: { label: techLabel?.label || "—", type: techScore > 0.3 ? "green" : techScore < -0.3 ? "red" : "amber" }, sub: "Nifty 200 weekly HA" },
              { title: "Combined",       score: combined,   lbl: combinedLabel, sub: "60% macro · 40% tech" },
            ].map((item, i) => (
              <div key={i} style={{ background: T.bg, border: `0.5px solid ${T.border}`, borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 11, color: T.text2, marginBottom: 6 }}>{item.title}</div>
                <div style={{ marginBottom: 8 }}><ScorePill label={item.lbl.label} type={item.lbl.type} /></div>
                <HedgeScoreBar score={item.score} threshold={0} color={labelColor(item.lbl.type)} />
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
                  <span style={{ fontSize: 10, color: T.text3 }}>{item.sub}</span>
                  <span style={{ fontSize: 10, color: T.text3, fontVariantNumeric: "tabular-nums" }}>{item.score.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Sell leg deployment status */}
          <Card style={{ marginBottom: 16 }}>
            <SectionHeader>Sell leg deployment status</SectionHeader>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
              {[
                { label: "Put sell leg",  score: putScore,  threshold: 0.40, strike: `${Math.round(NIFTY_SPOT * 0.88 / 50) * 50} PE`, desc: "12% OTM · below long put" },
                { label: "Call sell leg", score: callScore, threshold: 0.25, strike: `${Math.round(NIFTY_SPOT * 1.25 / 100) * 100} CE`, desc: "25% OTM · far upside" },
              ].map((leg, i) => {
                const active = leg.score >= leg.threshold;
                const color  = active ? T.green : T.red;
                const bg     = active ? T.greenBg : T.redBg;
                const fg     = active ? T.greenText : T.redText;
                return (
                  <div key={i} style={{ padding: "12px 14px", borderRadius: 10,
                    background: bg, border: `0.5px solid ${active ? T.green : T.red}30` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{leg.label}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: fg }}>
                        {active ? "DEPLOY" : "AVOID"}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: T.text2, marginBottom: 8 }}>{leg.strike} · {leg.desc}</div>
                    <HedgeScoreBar score={leg.score} threshold={leg.threshold} color={color} />
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
                      <span style={{ fontSize: 10, color: T.text3 }}>Score {(leg.score * 100).toFixed(0)}/100</span>
                      <span style={{ fontSize: 10, color: T.text3 }}>Threshold: {leg.threshold * 100}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Beta table */}
          <Card>
            <SectionHeader>Portfolio beta · 1-year lookback (252 trading days · industry standard)</SectionHeader>
            <div style={{ fontSize: 11, color: T.text2, marginBottom: 14, lineHeight: 1.7 }}>
              Beta recalculated at every rebalance using daily returns vs ^NSEI.
              Lots = (AUM × β) ÷ (Nifty spot × 75).
              1Y lookback balances recency with statistical reliability — minimum 252 data points.
            </div>
            {clients.length === 0 ? (
              <div style={{ fontSize: 12, color: T.text2 }}>Loading client data…</div>
            ) : clients.map((c, i) => {
              const beta    = c.beta || 1.10;
              const aum     = c.current_value || 1000000;
              const lots    = Math.max(1, Math.round((aum * beta) / (NIFTY_SPOT * LOT_SIZE)));
              const betaFg  = beta > 1.2 ? T.redText : beta < 0.9 ? T.greenText : T.text;
              const betaBg  = beta > 1.2 ? T.redBg   : beta < 0.9 ? T.greenBg   : T.bg2;
              return (
                <div key={c.client_id || i} style={{ display: "flex", alignItems: "center", gap: 12,
                  padding: "12px 0", borderBottom: `0.5px solid ${T.border}` }}>
                  <Avatar name={c.client_name} size={32} color={["blue","green","amber"][i % 3]} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{c.client_name}</div>
                    <div style={{ fontSize: 11, color: T.text2 }}>AUM {fmtL(aum)}</div>
                  </div>
                  <div style={{ textAlign: "center", padding: "4px 10px", borderRadius: 6, background: betaBg }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: betaFg }}>β {beta.toFixed(2)}</div>
                    <div style={{ fontSize: 10, color: T.text3 }}>beta</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: T.blue }}>{lots}</div>
                    <div style={{ fontSize: 10, color: T.text3 }}>lots</div>
                  </div>
                  <div style={{ fontSize: 11, color: T.text2, minWidth: 100, textAlign: "right" }}>
                    {beta > 1.2 ? "⚠ High β — tighter hedge" : beta < 0.9 ? "Low β — standard" : "Normal β"}
                  </div>
                </div>
              );
            })}
            <div style={{ marginTop: 10, padding: "8px 10px", background: T.blueBg, borderRadius: 8,
              fontSize: 11, color: T.blueText }}>
              Beta source: yfinance daily returns vs ^NSEI · Updated at every rebalance event
            </div>
          </Card>
        </div>
      )}

      {/* ── MACRO SIGNALS TAB ── */}
      {tab === "macro" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
            {/* Left: sliders */}
            <Card>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <SectionHeader>Signal inputs</SectionHeader>
                <ScorePill label={macroLabel.label} type={macroLabel.type} />
              </div>

              {/* Fetch status banner */}
              {dataLoading ? (
                <div style={{ fontSize: 11, color: T.text3, padding: "6px 10px", background: T.bg2,
                  borderRadius: 6, marginBottom: 14 }}>Fetching live market data…</div>
              ) : fetchError ? (
                <div style={{ fontSize: 11, color: T.amber, padding: "6px 10px", background: T.amberBg,
                  borderRadius: 6, marginBottom: 14 }}>
                  ⚠ Live fetch failed — showing last saved or default values. Adjust sliders manually.
                </div>
              ) : (
                <div style={{ fontSize: 11, color: T.green, padding: "6px 10px", background: T.greenBg,
                  borderRadius: 6, marginBottom: 14, display: "flex", justifyContent: "space-between" }}>
                  <span>✓ Live data loaded</span>
                  {fetchedAt && <span style={{ color: T.greenText }}>as of {fetchedAt}</span>}
                </div>
              )}

              {signals.map(s => (
                <div key={s.key} style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 12, color: T.text2 }}>{s.label}</span>
                      {s.autoFetched && (
                        <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 10,
                          color: T.green, fontWeight: 500 }}>
                          <span style={{ width: 5, height: 5, borderRadius: "50%",
                            background: T.green, display: "inline-block" }} />
                          live
                        </span>
                      )}
                      {s.manualOnly && (
                        <span style={{ fontSize: 10, color: T.amber, fontWeight: 500 }}>manual</span>
                      )}
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 500, color: T.text,
                      fontVariantNumeric: "tabular-nums" }}>{s.value}</span>
                  </div>
                  <input type="range" min={s.min} max={s.max} step={s.step} value={s.value}
                    onChange={e => updateSignal(s.key, e.target.value)}
                    style={{ width: "100%", accentColor: "#2196F3", cursor: "pointer" }} />
                </div>
              ))}
            </Card>

            {/* Right: individual scores */}
            <Card>
              <SectionHeader>Signal scores & weights</SectionHeader>
              {signals.map(s => {
                const sc = scoreMacroSignal(s.key, s.value);
                const pct = ((sc + 1) / 2) * 100;
                const color = sc > 0.3 ? T.green : sc > -0.1 ? T.amber : T.red;
                return (
                  <div key={s.key} style={{ padding: "8px 0", borderBottom: `0.5px solid ${T.border}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                      <div>
                        <span style={{ fontSize: 12, color: T.text }}>{s.label}</span>
                        <span style={{ fontSize: 10, color: T.text3, marginLeft: 6 }}>wt {(s.weight * 100).toFixed(0)}%</span>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 500, color, fontVariantNumeric: "tabular-nums" }}>
                        {sc > 0 ? "+" : ""}{sc.toFixed(2)}
                      </span>
                    </div>
                    <div style={{ position: "relative", height: 5, background: T.bg3, borderRadius: 3 }}>
                      <div style={{ position: "absolute", left: "50%", top: 0, height: "100%", width: 1, background: T.border2 }} />
                      <div style={{ position: "absolute", left: sc >= 0 ? "50%" : `${pct}%`, top: 0, height: "100%",
                        width: `${Math.abs(pct - 50)}%`, background: color, borderRadius: 3 }} />
                    </div>
                  </div>
                );
              })}
              {/* Composite total */}
              <div style={{ marginTop: 14, padding: "10px 12px", background: T.bg2, borderRadius: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 500 }}>Composite macro score</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: labelColor(macroLabel.type), fontVariantNumeric: "tabular-nums" }}>{macroScore.toFixed(3)}</span>
                </div>
                <HedgeScoreBar score={macroScore} threshold={0} color={labelColor(macroLabel.type)} />
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                  <span style={{ fontSize: 9, color: T.text3 }}>Strongly Bearish (−1)</span>
                  <span style={{ fontSize: 9, color: T.text3 }}>Strongly Bullish (+1)</span>
                </div>
              </div>
            </Card>
          </div>

          {/* Signal guide */}
          <Card style={{ marginTop: 16 }}>
            <SectionHeader>Signal guide</SectionHeader>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: 10 }}>
              {[
                { sig: "India VIX",      green: "13–18: ideal sells",  red: ">20: avoid short put" },
                { sig: "IV Rank",        green: ">45: rich premium",   red: "<30: not worth selling" },
                { sig: "Put-Call Ratio", green: "0.7–1.1: balanced",   red: ">1.3: panic — stay out" },
                { sig: "Nifty RSI",      green: "38–65: neutral",      red: "<35 or >75: extremes" },
                { sig: "FII Flow",       green: "+1: strong buying",   red: "−1: heavy selling" },
                { sig: "Fed Surprise",   green: "+1: dovish surprise",  red: "−1: hawkish shock" },
                { sig: "INR/USD",        green: "+1: stable/firm",     red: "−1: rapid drop" },
                { sig: "Brent Crude",    green: "+1: flat/falling",    red: "−1: spike >5%/wk" },
              ].map((g, i) => (
                <div key={i} style={{ padding: "10px 12px", background: T.bg2, borderRadius: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>{g.sig}</div>
                  <div style={{ fontSize: 11, color: T.green, marginBottom: 3 }}>✓ {g.green}</div>
                  <div style={{ fontSize: 11, color: T.red }}>✗ {g.red}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Fed Surprise breakdown panel */}
          {fedDetail && (
            <Card style={{ marginTop: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <SectionHeader>Fed surprise — auto-calculated breakdown</SectionHeader>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: T.green, fontWeight: 500 }}>
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: T.green, display: "inline-block" }} />
                  live · ZQ=F + ^GSPC + DXY
                </div>
              </div>

              {/* FOMC timing */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                <div style={{ background: T.bg2, borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ fontSize: 11, color: T.text2, marginBottom: 4 }}>Last FOMC meeting</div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{fedDetail.last_fomc}</div>
                  <div style={{ fontSize: 11, color: T.text3 }}>{fedDetail.days_since_fomc} days ago</div>
                </div>
                <div style={{ background: T.bg2, borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ fontSize: 11, color: T.text2, marginBottom: 4 }}>Next FOMC meeting</div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{fedDetail.next_fomc}</div>
                  <div style={{ fontSize: 11, color: T.text3 }}>{fedDetail.days_to_next_fomc} days away</div>
                </div>
              </div>

              {/* Three components */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                {[
                  {
                    label: "Rate surprise (ZQ=F futures)",
                    weight: "50%",
                    score: fedDetail.rate_surprise,
                    detail: fedDetail.futures_detail,
                    desc: "Fed Funds Futures implied rate shift before vs after meeting",
                  },
                  {
                    label: "S&P 500 reaction",
                    weight: "30%",
                    score: fedDetail.sp500_reaction,
                    detail: fedDetail.sp500_detail,
                    desc: "Market read of the decision — up = dovish, down = hawkish",
                  },
                  {
                    label: "DXY Dollar Index reaction",
                    weight: "20%",
                    score: fedDetail.dxy_reaction,
                    detail: fedDetail.dxy_detail,
                    desc: "Dollar weakens on dovish surprise, strengthens on hawkish",
                  },
                ].map((comp, i) => {
                  const color = comp.score > 0.2 ? T.green : comp.score < -0.2 ? T.red : T.amber;
                  const bg    = comp.score > 0.2 ? T.greenBg : comp.score < -0.2 ? T.redBg : T.amberBg;
                  return (
                    <div key={i} style={{ padding: "10px 12px", borderRadius: 8, background: bg,
                      border: `0.5px solid ${color}30` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                        <div>
                          <span style={{ fontSize: 12, fontWeight: 500 }}>{comp.label}</span>
                          <span style={{ fontSize: 10, color: T.text3, marginLeft: 6 }}>weight {comp.weight}</span>
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color,
                          fontVariantNumeric: "tabular-nums" }}>
                          {comp.score > 0 ? "+" : ""}{comp.score?.toFixed(2)}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: T.text2, marginBottom: 3 }}>{comp.desc}</div>
                      <div style={{ fontSize: 10, color: T.text3, fontFamily: "monospace" }}>{comp.detail}</div>
                    </div>
                  );
                })}
              </div>

              {/* Interpretation */}
              <div style={{ padding: "10px 14px", background: T.bg2, borderRadius: 8,
                border: `0.5px solid ${T.border}` }}>
                <div style={{ fontSize: 11, color: T.text2, marginBottom: 4 }}>Interpretation</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{fedDetail.interpretation}</div>
                <div style={{ fontSize: 11, color: T.text3, marginTop: 4 }}>
                  Composite = (rate surprise × 0.50) + (S&P reaction × 0.30) + (DXY reaction × 0.20)
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ── TECH ZONE TAB ── */}
      {tab === "tech" && (
        <div>
          <Card style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Nifty 200 — weekly Heiken Ashi</div>
                <div style={{ fontSize: 12, color: T.text2 }}>Supertrend (14, 2) · EMA 20 · Update every Monday after weekly close</div>
              </div>
              <ScorePill label={techLabel?.label || "—"} type={techScore > 0.3 ? "green" : techScore < -0.3 ? "red" : "amber"} />
            </div>
            {TECH_ZONES.map(z => {
              const isSelected = techZone === z.key;
              const zcolor = z.score > 0.3 ? T.green : z.score < -0.3 ? T.red : T.amber;
              const zbg    = z.score > 0.3 ? T.greenBg : z.score < -0.3 ? T.redBg : T.amberBg;
              const zfg    = z.score > 0.3 ? T.greenText : z.score < -0.3 ? T.redText : T.amberText;
              return (
                <div key={z.key} onClick={() => setTechZone(z.key)}
                  style={{ padding: "12px 14px", borderRadius: 10, marginBottom: 8, cursor: "pointer",
                    border: isSelected ? `1px solid ${zcolor}` : `0.5px solid ${T.border}`,
                    background: isSelected ? zbg : T.bg, transition: "all .15s" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {isSelected && <div style={{ width: 6, height: 6, borderRadius: "50%", background: zcolor }} />}
                      <span style={{ fontSize: 13, fontWeight: 500, color: isSelected ? zfg : T.text }}>{z.label}</span>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: zcolor }}>Score {z.score > 0 ? "+" : ""}{z.score}</span>
                  </div>
                  <div style={{ fontSize: 11, color: T.text2, marginBottom: isSelected ? 8 : 0, lineHeight: 1.6 }}>{z.desc}</div>
                  {isSelected && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 4 }}>
                      <div style={{ fontSize: 11, color: T.text2, background: T.bg2, borderRadius: 6, padding: "6px 8px" }}>
                        <span style={{ fontWeight: 500 }}>Put sell: </span>{z.putSell}
                      </div>
                      <div style={{ fontSize: 11, color: T.text2, background: T.bg2, borderRadius: 6, padding: "6px 8px" }}>
                        <span style={{ fontWeight: 500 }}>Call sell: </span>{z.callSell}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </Card>

          {/* Combined output */}
          <Card>
            <SectionHeader>Combined score breakdown</SectionHeader>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
              {[
                { title: "Macro (60%)", lbl: macroLabel, val: macroScore },
                { title: "Technical (40%)", lbl: { label: techLabel?.label || "—", type: techScore > 0.3 ? "green" : techScore < -0.3 ? "red" : "amber" }, val: techScore },
                { title: "Combined", lbl: combinedLabel, val: combined },
              ].map((item, i) => (
                <div key={i} style={{ background: T.bg2, borderRadius: 8, padding: "12px 14px" }}>
                  <div style={{ fontSize: 11, color: T.text2, marginBottom: 6 }}>{item.title}</div>
                  <div style={{ marginBottom: 6 }}><ScorePill label={item.lbl.label} type={item.lbl.type} /></div>
                  <div style={{ fontSize: 11, color: T.text3, fontVariantNumeric: "tabular-nums" }}>{item.val.toFixed(3)}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* ── CLIENT POSITIONS TAB ── */}
      {tab === "clients" && (
        <div>
          <div style={{ padding: "8px 12px", background: T.amberBg, borderRadius: 8, marginBottom: 16,
            fontSize: 11, color: T.amberText, lineHeight: 1.6 }}>
            ⚠ Advisory only — client executes all option positions via Zerodha. No orders are placed on their behalf.
          </div>

          {clients.length === 0 ? <Spinner /> : clients.map((c, i) => {
            const beta       = c.beta || 1.10;
            const aum        = c.current_value || 1000000;
            const maxLots    = Math.max(1, Math.round((aum * beta) / (NIFTY_SPOT * LOT_SIZE)));
            const putLots    = getLotCount(putScore, maxLots, 0.40);
            const callLots   = getLotCount(callScore, maxLots, 0.25);
            const lpStrike   = Math.round(NIFTY_SPOT * 0.95 / 50) * 50;
            const spStrike   = Math.round(NIFTY_SPOT * 0.88 / 50) * 50;
            const scStrike   = Math.round(NIFTY_SPOT * 1.25 / 100) * 100;

            let summary, summaryType;
            if (putDeployed && callDeployed)  { summary = "Deploy both sell legs";     summaryType = "green"; }
            else if (putDeployed)              { summary = "Put sell only — bullish bias"; summaryType = "green"; }
            else if (callDeployed)             { summary = "Call sell only — bearish bias"; summaryType = "amber"; }
            else                              { summary = "Danger mode — long put only"; summaryType = "red"; }

            const actions = [
              { type: "HOLD",   label: `Long ${lpStrike} PE`,    lots: maxLots,  color: T.green,   bg: T.greenBg,  note: "Permanent skeleton — do not close" },
              { type: putDeployed ? "SELL" : "AVOID",  label: `Sell ${spStrike} PE`, lots: putLots,  color: putDeployed ? T.blue : T.red,   bg: putDeployed ? T.blueBg : T.redBg,  note: `Put score ${(putScore*100).toFixed(0)}/100 · threshold 40` },
              { type: callDeployed ? "SELL" : "AVOID", label: `Sell ${scStrike} CE`, lots: callLots, color: callDeployed ? T.blue : T.red, bg: callDeployed ? T.blueBg : T.redBg, note: `Call score ${(callScore*100).toFixed(0)}/100 · threshold 25` },
            ];

            return (
              <Card key={c.client_id || i} style={{ marginBottom: 12 }}>
                {/* Client header */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                  <Avatar name={c.client_name} size={36} color={["blue","green","amber"][i % 3]} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{c.client_name}</div>
                    <div style={{ fontSize: 11, color: T.text2 }}>
                      AUM {fmtL(aum)} · β {beta.toFixed(2)} · {maxLots} lots max
                    </div>
                  </div>
                  <ScorePill label={summary} type={summaryType} />
                </div>

                {/* Put + call score bars */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                  {[
                    { label: "Put sell score", score: putScore,  threshold: 0.40, active: putDeployed  },
                    { label: "Call sell score", score: callScore, threshold: 0.25, active: callDeployed },
                  ].map((bar, j) => (
                    <div key={j}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: T.text2 }}>{bar.label}</span>
                        <span style={{ fontSize: 11, fontWeight: 500, color: bar.active ? T.green : T.red }}>
                          {(bar.score * 100).toFixed(0)}/100
                        </span>
                      </div>
                      <HedgeScoreBar score={bar.score} threshold={bar.threshold} color={bar.active ? T.green : T.red} />
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {actions.map((a, j) => (
                    <div key={j} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "9px 12px", borderRadius: 8, background: a.bg,
                      border: `0.5px solid ${a.color}30` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".04em",
                          color: a.color, background: `${a.color}18`,
                          padding: "2px 6px", borderRadius: 4 }}>{a.type}</span>
                        <span style={{ fontSize: 12, fontWeight: 500 }}>{a.label}</span>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        {a.lots > 0 && (
                          <div style={{ fontSize: 12, fontWeight: 600, color: a.color }}>
                            {a.lots} lot{a.lots !== 1 ? "s" : ""}
                          </div>
                        )}
                        <div style={{ fontSize: 10, color: T.text3 }}>{a.note}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}

          {/* Lot sizing rules */}
          <Card style={{ marginTop: 4 }}>
            <SectionHeader>Lot sizing rules</SectionHeader>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 6 }}>
              {[
                ["Put sell > 0.80", "100% of lots"],   ["Call sell > 0.60", "100% of lots"],
                ["Put sell 0.60–0.80", "75% of lots"], ["Call sell 0.40–0.60", "75% of lots"],
                ["Put sell 0.40–0.60", "50% of lots"], ["Call sell 0.25–0.40", "50% of lots"],
                ["Put sell < 0.40", "Avoid"],           ["Call sell < 0.25", "Avoid"],
              ].map(([cond, action], i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between",
                  fontSize: 11, padding: "6px 10px", background: T.bg2, borderRadius: 6 }}>
                  <span style={{ color: T.text2 }}>{cond}</span>
                  <span style={{ fontWeight: 500, color: action === "Avoid" ? T.red : T.text }}>{action}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

const ADMIN_NAV = [
  { id: "dashboard",  label: "Dashboard",   icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg> },
  { id: "clients",    label: "Clients",     icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><path d="M21 21v-2a4 4 0 0 0-3-3.85"/></svg> },
  { id: "rebalance",  label: "Rebalance",   icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><polyline points="23 20 23 14 17 14"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15"/></svg> },
  { id: "history",    label: "History",     icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> },
  { id: "kyc_review", label: "KYC Review",  icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg> },
  { id: "analytics",  label: "Performance", icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg> },
  { id: "grievances",  label: "Grievances",       icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> },
  { id: "hedge_intel", label: "Hedge Intelligence", icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> },
];

// ─── MODEL PORTFOLIO PAGE (available to all logged-in clients) ────────────────
function ModelPortfolioPage({ token, accessStatus, onGoKyc }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/strategy/model-portfolio", {}, token)
      .then(setData).catch(console.error).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <Spinner />;

  // No portfolio yet — this is a brand-new client who hasn't completed KYC.
  // Show a warm, on-brand welcome screen instead of a raw error message.
  if (!data) return (
    <div style={{ padding: "32px 24px", maxWidth: 560 }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 22, fontWeight: 600, color: T.text, marginBottom: 8, lineHeight: 1.3 }}>
          Welcome to ThinkWealth 👋
        </div>
        <div style={{ fontSize: 14, color: T.text2, lineHeight: 1.7 }}>
          Money matters — not because wealth is the goal, but because financial security is what lets you live life on your own terms, protect the people you love, and face the future without fear.
        </div>
      </div>

      {/* Three "why plan now" philosophy cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 28 }}>
        {[
          {
            icon: "🏠",
            title: "Your family is counting on you",
            body: "Whether it's your parents, your partner, or your children — the people who depend on you deserve a plan, not just good intentions. Wealth planning is an act of responsibility, not luxury.",
          },
          {
            icon: "🌱",
            title: "The best time to start is now",
            body: "Every month you wait is compounding working against you instead of for you. A small, disciplined investment today grows into meaningful security tomorrow — for your goals, your milestones, and your peace of mind.",
          },
          {
            icon: "🧭",
            title: "You deserve a clear strategy",
            body: "Not a product push. Not a one-size-fits-all mutual fund. A real, personalized plan built around your income, your risk appetite, and your life goals — reviewed and rebalanced as your life evolves.",
          },
        ].map(({ icon, title, body }) => (
          <div key={title} style={{
            background: T.bg2, borderRadius: 12, padding: "16px 18px",
            display: "flex", gap: 14, alignItems: "flex-start",
            border: `0.5px solid ${T.border}`,
          }}>
            <div style={{ fontSize: 24, lineHeight: 1, flexShrink: 0, marginTop: 2 }}>{icon}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 4 }}>{title}</div>
              <div style={{ fontSize: 12, color: T.text2, lineHeight: 1.65 }}>{body}</div>
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div style={{
        background: T.text, borderRadius: 12, padding: "20px 22px",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
        flexWrap: "wrap",
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.bg, marginBottom: 4 }}>
            Ready to take the first step?
          </div>
          <div style={{ fontSize: 12, color: T.bg, opacity: 0.65, lineHeight: 1.5 }}>
            Complete your KYC — it takes about 10 minutes and sets the foundation for everything that follows.
          </div>
        </div>
        <Btn variant="primary" onClick={onGoKyc} style={{
          background: T.bg, color: T.text, border: "none",
          padding: "10px 20px", fontSize: 13, fontWeight: 600,
          whiteSpace: "nowrap", flexShrink: 0,
        }}>
          Start KYC →
        </Btn>
      </div>

      {/* Subtle reassurance footer */}
      <div style={{ marginTop: 16, fontSize: 11, color: T.text3, textAlign: "center", lineHeight: 1.6 }}>
        SEBI RIA registration in progress · Your data is encrypted and stored securely · SCORES grievance redressal available
      </div>

    </div>
  );

  const locked = !accessStatus?.full_access;

  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 4 }}>{data.strategy_name}</div>
      <div style={{ fontSize: 12, color: T.text2, marginBottom: 20 }}>
        {data.description} {data.effective_date && `· Last rebalanced ${data.effective_date}`}
      </div>

      {locked && (
        <div style={{ background: T.amberBg, border: `0.5px solid ${T.amber}`, borderRadius: 10, padding: "14px 16px", marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.amberText, marginBottom: 2 }}>Complete KYC to start investing</div>
            <div style={{ fontSize: 12, color: T.amberText, opacity: 0.85 }}>
              You can see the model portfolio below. To invest and execute trades, complete your KYC and agree to the advisory fee.
            </div>
          </div>
          <Btn variant="primary" onClick={onGoKyc} style={{ whiteSpace: "nowrap" }}>Complete KYC</Btn>
        </div>
      )}

      <Card>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 14 }}>Holdings — {data.holdings?.length || 0} stocks</div>
        {(data.holdings || []).map((h, i) => (
          <div key={h.isin} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < data.holdings.length - 1 ? `0.5px solid ${T.border}` : "none" }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: T.bg2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: T.text2, flexShrink: 0 }}>
              {(h.symbol || h.company_name || "").slice(0, 3)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{h.company_name}</div>
              <div style={{ fontSize: 11, color: T.text2 }}>{h.symbol} {h.sector ? `· ${h.sector}` : ""}</div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{h.weight_pct?.toFixed(1)}%</div>
              <div style={{ fontSize: 10, color: T.text2 }}>target weight</div>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ─── LOCKED PAGE (shown when KYC not done) ────────────────────────────────────
function LockedPage({ onGoKyc }) {
  return (
    <div style={{ padding: "60px 20px", textAlign: "center" }}>
      <div style={{ fontSize: 36, marginBottom: 16 }}>🔒</div>
      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>KYC required</div>
      <div style={{ fontSize: 13, color: T.text2, marginBottom: 24, maxWidth: 320, margin: "0 auto 24px" }}>
        This section is available after your KYC is verified and you agree to the advisory fee.
      </div>
      <Btn variant="primary" onClick={onGoKyc}>Go to KYC & Onboarding</Btn>
    </div>
  );
}

const CLIENT_NAV = [
  { id: "model_portfolio", label: "Model portfolio",   icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></svg> },
  { id: "kyc",             label: "KYC & Onboarding", icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
  { id: "portfolio",       label: "My portfolio",      icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg> },
  { id: "portfolios",      label: "All portfolios",    icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg> },
  { id: "rebalance",       label: "Execute rebalance", icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><polyline points="23 20 23 14 17 14"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15"/></svg> },
  { id: "sip",             label: "Add funds / SIP",   icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> },
  { id: "grievance",       label: "Raise grievance",   icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> },
];

// ─── REBALANCE HISTORY PAGE ──────────────────────────────────────────────────
function RebalanceHistoryPage({ token, isMobile }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [orders, setOrders] = useState({});
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    apiFetch("/admin/rebalance/history", {}, token)
      .then(d => setHistory(d.rebalances || [])).catch(console.error).finally(() => setLoading(false));
  }, [token]);

  const loadOrders = async (rebalanceId) => {
    if (orders[rebalanceId]) { setExpanded(expanded === rebalanceId ? null : rebalanceId); return; }
    const res = await apiFetch(`/admin/rebalance/${rebalanceId}/orders`, {}, token);
    setOrders(prev => ({ ...prev, [rebalanceId]: res.baskets || [] }));
    setExpanded(rebalanceId);
  };

  const cancelRebalance = async (rebalanceId) => {
    if (!window.confirm("Cancel this rebalance? All pending order baskets will be skipped.")) return;
    setCancelling(rebalanceId);
    try {
      await apiFetch(`/admin/rebalance/${rebalanceId}/cancel`, { method: "POST" }, token);
      setHistory(prev => prev.map(r => r.id === rebalanceId ? { ...r, status: "cancelled" } : r));
    } catch(e) { alert("Cancel failed: " + e.message); } finally { setCancelling(null); }
  };

  if (loading) return <Spinner />;
  const statusColor = (s) => s === "published" ? T.blueText : s === "completed" ? T.green : s === "cancelled" ? T.red : T.text2;

  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 20 }}>Rebalance history</div>
      {history.length === 0 && <div style={{ color: T.text2, fontSize: 13 }}>No rebalances published yet.</div>}
      {history.map(r => (
        <Card key={r.id} style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
            <div>
              <div style={{ fontWeight: 500, fontSize: 14 }}>{r.rebalance_date}</div>
              <div style={{ fontSize: 12, color: T.text2, marginTop: 2 }}>Published: {r.published_at ? new Date(r.published_at).toLocaleString("en-IN") : "—"}</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>
                <span style={{ color: statusColor(r.status), fontWeight: 500, textTransform: "capitalize" }}>{r.status}</span>
                <span style={{ color: T.text2, marginLeft: 12 }}>{r.basket_count} client{r.basket_count !== 1 ? "s" : ""} · {r.executed_count} executed · {r.pending_count} pending</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {(r.status === "published" || r.status === "pending") && (
                <Btn onClick={() => cancelRebalance(r.id)} disabled={cancelling === r.id}
                  style={{ fontSize: 12, color: T.red, border: `1px solid ${T.red}`, background: "transparent" }}>
                  {cancelling === r.id ? "Cancelling..." : "Cancel"}
                </Btn>
              )}
              <Btn onClick={() => loadOrders(r.id)} style={{ fontSize: 12 }}>{expanded === r.id ? "Hide orders" : "View orders"}</Btn>
            </div>
          </div>

          {expanded === r.id && orders[r.id] && (
            <div style={{ marginTop: 16, borderTop: `0.5px solid ${T.border}`, paddingTop: 16 }}>
              {orders[r.id].map((basket, bi) => (
                <div key={bi} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                    {basket.portfolio_name}
                    <Badge type={basket.basket_status === "executed" ? "green" : basket.basket_status === "skipped" ? "amber" : "blue"}>{basket.basket_status}</Badge>
                  </div>
                  {(basket.orders || []).map((o, oi) => (
                    <div key={oi} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 0", borderBottom: `0.5px solid ${T.border}` }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{o.symbol}</div>
                        <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>{o.broker_order_id || "—"}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>
                          <span style={{ color: o.action === "BUY" ? T.green : T.red, fontWeight: 600 }}>{o.action}</span>
                          <span style={{ color: T.text2, marginLeft: 6 }}>{o.quantity} qty · ₹{fmt(o.estimated_value)}</span>
                        </div>
                        <Badge type={o.fill_status === "filled" ? "green" : o.fill_status === "rejected" ? "red" : "amber"}>{o.fill_status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

// ─── KYC ONBOARDING WIZARD ───────────────────────────────────────────────────
function KYCPage({ token, auth, isMobile }) {
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState({});
  const [riskProfile, setRiskProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiFetch("/kyc/profile", {}, token),
      apiFetch("/kyc/documents", {}, token),
      apiFetch("/kyc/risk-profile", {}, token),
    ]).then(([p, d, r]) => {
      setProfile(p.profile); setDocuments(d.documents || {}); setRiskProfile(r.profile);
      if (p.profile?.kyc_status === 'submitted' || p.profile?.kyc_status === 'verified') setStep(5);
      else if (r.profile) setStep(4);
      else if (Object.keys(d.documents || {}).length >= 3) setStep(3);
      else if (p.profile) setStep(2);
    }).catch(console.error).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <Spinner />;

  // Shorter step labels on mobile so they fit
  const steps = isMobile ? ["Info", "Docs", "Risk", "Terms", "Done"] : ["Personal details", "Documents", "Risk profile", "Agreement", "Complete"];

  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 4 }}>KYC & Onboarding</div>
      <div style={{ fontSize: 12, color: T.text2, marginBottom: 24 }}>Complete your KYC to start investing</div>

      <div style={{ display: "flex", marginBottom: 32 }}>
        {steps.map((s, i) => (
          <div key={i} style={{ flex: 1, textAlign: "center" }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", margin: "0 auto 6px",
              background: i + 1 < step ? T.green : i + 1 === step ? T.blue : T.border,
              color: i + 1 <= step ? "#fff" : T.text2,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600 }}>
              {i + 1 < step ? "✓" : i + 1}
            </div>
            <div style={{ fontSize: 10, color: i + 1 === step ? T.blue : T.text2 }}>{s}</div>
          </div>
        ))}
      </div>

      {step === 1 && <KYCStep1 token={token} profile={profile} isMobile={isMobile} onNext={(p) => { setProfile(p); setStep(2); }} />}
      {step === 2 && <KYCStep2 token={token} documents={documents} onNext={(d) => { setDocuments(d); setStep(3); }} onBack={() => setStep(1)} />}
      {step === 3 && <KYCStep3 token={token} onNext={(r) => { setRiskProfile(r); setStep(4); }} onBack={() => setStep(2)} />}
      {step === 4 && <KYCStep4 token={token} profile={profile} riskProfile={riskProfile} onNext={() => setStep(5)} onBack={() => setStep(3)} />}
      {step === 5 && <KYCComplete profile={profile} />}
    </div>
  );
}

// Shared form field components for the KYC wizard.
// IMPORTANT: these must stay OUTSIDE any component that re-renders on every
// keystroke (like KYCStep1). Defining them inside such a component creates a
// brand new function on every render, which makes React unmount/remount the
// actual <input> DOM element each time the user types a character — that is
// what was causing the cursor/focus to jump out of the field after every key.
const KYCInput = ({ label, value, onChange, placeholder, required, type = "text" }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ fontSize: 12, color: T.text2, display: "block", marginBottom: 4 }}>{label} {required && <span style={{ color: T.red }}>*</span>}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{ width: "100%", padding: "8px 10px", border: `0.5px solid ${T.border2}`, borderRadius: 8, background: T.bg, color: T.text, fontSize: 13, boxSizing: "border-box" }} />
  </div>
);

const KYCSelect = ({ label, value, onChange, options, required }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ fontSize: 12, color: T.text2, display: "block", marginBottom: 4 }}>{label} {required && <span style={{ color: T.red }}>*</span>}</label>
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ width: "100%", padding: "8px 10px", border: `0.5px solid ${T.border2}`, borderRadius: 8, background: T.bg, color: T.text, fontSize: 13 }}>
      <option value="">Select...</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

function KYCStep1({ token, profile, onNext, isMobile }) {
  const [form, setForm] = useState({
    full_name: profile?.full_name || "", date_of_birth: profile?.date_of_birth || "",
    pan_number: profile?.pan_number || "", aadhaar_last4: profile?.aadhaar_last4 || "",
    address: profile?.address || "", city: profile?.city || "",
    state: profile?.state || "", pincode: profile?.pincode || "",
    mobile: profile?.mobile || "", occupation: profile?.occupation || "",
    annual_income: profile?.annual_income || "", bank_name: profile?.bank_name || "",
    bank_account_number: profile?.bank_account_number || "", bank_ifsc: profile?.bank_ifsc || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.full_name || !form.pan_number || !form.mobile) { alert("Please fill all required fields"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${API_BASE}/kyc/profile`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (data.success) onNext(form); else alert("Error: " + data.detail);
    } catch(e) { alert(e.message); } finally { setSaving(false); }
  };

  // On mobile everything is 1 column; desktop uses 2 or 3 cols
  const cols2 = isMobile ? "1fr" : "1fr 1fr";
  const cols3 = isMobile ? "1fr" : "1fr 1fr 1fr";

  return (
    <Card>
      <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 20 }}>Personal & Financial Details</div>
      <div style={{ display: "grid", gridTemplateColumns: cols2, gap: "0 20px" }}>
        <KYCInput label="Full name (as per PAN)" value={form.full_name} onChange={v => set("full_name", v)} placeholder="e.g. Rahul Sharma" required />
        <KYCInput label="Date of birth" value={form.date_of_birth} onChange={v => set("date_of_birth", v)} type="date" required />
        <KYCInput label="PAN number" value={form.pan_number} onChange={v => set("pan_number", v)} placeholder="ABCDE1234F" required />
        <KYCInput label="Aadhaar last 4 digits" value={form.aadhaar_last4} onChange={v => set("aadhaar_last4", v)} placeholder="1234" required />
        <KYCInput label="Mobile number" value={form.mobile} onChange={v => set("mobile", v)} placeholder="9876543210" required />
        <KYCSelect label="Annual income" value={form.annual_income} onChange={v => set("annual_income", v)} required options={["Below ₹1 Lakh", "₹1-5 Lakh", "₹5-10 Lakh", "₹10-25 Lakh", "₹25-50 Lakh", "₹50 Lakh - ₹1 Crore", "Above ₹1 Crore"]} />
        <KYCSelect label="Occupation" value={form.occupation} onChange={v => set("occupation", v)} required options={["Salaried", "Self-employed", "Business owner", "Professional", "Retired", "Student", "Homemaker", "Other"]} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, margin: "16px 0 12px", color: T.text2 }}>Address</div>
      <KYCInput label="Street address" value={form.address} onChange={v => set("address", v)} placeholder="House no, Street, Area" required />
      <div style={{ display: "grid", gridTemplateColumns: cols3, gap: "0 20px" }}>
        <KYCInput label="City" value={form.city} onChange={v => set("city", v)} placeholder="Mumbai" required />
        <KYCInput label="State" value={form.state} onChange={v => set("state", v)} placeholder="Maharashtra" required />
        <KYCInput label="Pincode" value={form.pincode} onChange={v => set("pincode", v)} placeholder="400001" required />
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, margin: "16px 0 12px", color: T.text2 }}>Bank Details</div>
      <div style={{ display: "grid", gridTemplateColumns: cols3, gap: "0 20px" }}>
        <KYCInput label="Bank name" value={form.bank_name} onChange={v => set("bank_name", v)} placeholder="HDFC Bank" required />
        <KYCInput label="Account number" value={form.bank_account_number} onChange={v => set("bank_account_number", v)} placeholder="1234567890" required />
        <KYCInput label="IFSC code" value={form.bank_ifsc} onChange={v => set("bank_ifsc", v)} placeholder="HDFC0001234" required />
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
        <Btn variant="primary" onClick={handleSubmit} disabled={saving} style={{ width: isMobile ? "100%" : undefined }}>
          {saving ? "Saving..." : "Save & Continue →"}
        </Btn>
      </div>
    </Card>
  );
}

function KYCStep2({ token, documents, onNext, onBack }) {
  const [docs, setDocs] = useState(documents);
  const [uploading, setUploading] = useState({});
  // One ref per doc type, so each "Upload" button can trigger its own hidden
  // file input directly via .click(). A <label> wrapping a <button> does NOT
  // reliably forward clicks to a nested <input type="file"> — the <button>'s
  // own click behavior intercepts the event first, which is why clicking
  // Upload appeared to do nothing.
  const fileInputRefs = useRef({});

  const docTypes = [
    { key: "pan", label: "PAN Card", desc: "Clear photo or scan of your PAN card", required: true },
    { key: "aadhaar", label: "Aadhaar Card (masked)", desc: "Aadhaar with number masked/hidden", required: true },
    { key: "bank_proof", label: "Bank Proof", desc: "Cancelled cheque or bank statement", required: true },
    { key: "photo", label: "Photograph", desc: "Recent passport-size photograph", required: true },
    { key: "income_proof", label: "Income Proof", desc: "Salary slip, ITR, or Form 16", required: false },
    { key: "signature", label: "Signature", desc: "Signature on white paper, scanned", required: true },
  ];

  const uploadDoc = async (docType, file) => {
    setUploading(u => ({ ...u, [docType]: true }));
    try {
      const fd = new FormData(); fd.append("doc_type", docType); fd.append("file", file);
      const res = await fetch(`${API_BASE}/kyc/document/upload`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (data.success) setDocs(d => ({ ...d, [docType]: { file_name: file.name, status: "uploaded" } }));
      else alert("Upload failed: " + data.detail);
    } catch(e) { alert(e.message); } finally { setUploading(u => ({ ...u, [docType]: false })); }
  };

  const requiredDone = docTypes.filter(d => d.required).every(d => docs[d.key]);

  return (
    <Card>
      <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>Document Upload</div>
      <div style={{ fontSize: 12, color: T.text2, marginBottom: 20 }}>Upload clear, legible copies. JPG, PNG, PDF (max 10MB each)</div>
      {docTypes.map(dt => (
        <div key={dt.key} style={{ marginBottom: 16, padding: 14, borderRadius: 10,
          border: `0.5px solid ${docs[dt.key] ? T.green : T.border}`, background: docs[dt.key] ? "#F0FDF4" : T.bg }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{dt.label} {dt.required && <span style={{ color: T.red }}>*</span>}</div>
              <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>{dt.desc}</div>
              {docs[dt.key] && <div style={{ fontSize: 11, color: T.green, marginTop: 4 }}>✓ {docs[dt.key].file_name}</div>}
            </div>
            <div style={{ flexShrink: 0 }}>
              {uploading[dt.key] ? <div style={{ fontSize: 12, color: T.text2 }}>Uploading...</div> : (
                <>
                  <Btn style={{ fontSize: 12 }} onClick={() => fileInputRefs.current[dt.key]?.click()}>
                    {docs[dt.key] ? "Replace" : "Upload"}
                  </Btn>
                  <input type="file" accept=".jpg,.jpeg,.png,.pdf" style={{ display: "none" }}
                    ref={el => { fileInputRefs.current[dt.key] = el; }}
                    onChange={e => { e.target.files[0] && uploadDoc(dt.key, e.target.files[0]); e.target.value = ""; }} />
                </>
              )}
            </div>
          </div>
        </div>
      ))}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
        <Btn onClick={onBack}>← Back</Btn>
        <Btn variant="primary" onClick={() => onNext(docs)} disabled={!requiredDone}>
          {requiredDone ? "Continue →" : "Upload required docs"}
        </Btn>
      </div>
    </Card>
  );
}

const RISK_QUESTIONS = [
  { id: 1, section: "A", text: "What is your age bracket?", options: ["60+", "50-60", "40-50", "30-40", "Under 30"] },
  { id: 2, section: "A", text: "What is your annual household income?", options: ["Under ₹5L", "₹5-15L", "₹15-30L", "₹30-50L", "₹50L+"] },
  { id: 3, section: "A", text: "What is your monthly surplus after all expenses?", options: ["Under ₹10K", "₹10-30K", "₹30-50K", "₹50K+"] },
  { id: 4, section: "A", text: "What are your existing financial liabilities (loans etc.)?", options: ["High (EMI > 50% income)", "Medium (EMI 25-50%)", "Low (EMI < 25%)", "None"] },
  { id: 5, section: "B", text: "What is your primary investment goal?", options: ["Income generation", "Capital preservation", "Wealth creation", "Aggressive growth"] },
  { id: 6, section: "B", text: "What is your investment time horizon?", options: ["Under 2 years", "2-5 years", "5-10 years", "10+ years"] },
  { id: 7, section: "B", text: "When will you likely need this invested money?", options: ["Within 1 year", "1-3 years", "3-7 years", "7+ years"] },
  { id: 8, section: "B", text: "Do you have an emergency fund covering 6 months of expenses?", options: ["No", "Partially", "Yes — 3 months", "Yes — 6+ months"] },
  { id: 9, section: "C", text: "If your portfolio fell 20% in 3 months, you would:", options: ["Sell everything immediately", "Sell some to reduce risk", "Hold and wait", "Buy more — great opportunity"] },
  { id: 10, section: "C", text: "Which scenario would you prefer?", options: ["Certain 5% return", "60% chance of 15% or 0%", "50% chance of 25% or -5%", "40% chance of 40% or -15%"] },
  { id: 11, section: "C", text: "How would you feel if your investment dropped ₹1L temporarily?", options: ["Extremely stressed — would sell", "Very uncomfortable", "Somewhat uncomfortable but would hold", "Fine — temporary drops are normal"] },
  { id: 12, section: "C", text: "Your investment philosophy is closest to:", options: ["Capital preservation above all", "Steady growth with low risk", "Growth with some risk", "Maximum returns — risk is fine"] },
  { id: 13, section: "C", text: "In a market crash like March 2020, your instinct would be:", options: ["Exit markets completely", "Move to safer assets", "Stay invested and wait", "Invest more aggressively"] },
  { id: 14, section: "D", text: "Your experience with equity investing:", options: ["None", "Under 2 years", "2-5 years", "5+ years"] },
  { id: 15, section: "D", text: "Which investments have you made before?", options: ["FD/RD only", "Mutual funds only", "Direct stocks", "PMS/AIF/Derivatives"] },
  { id: 16, section: "D", text: "How would you rate your knowledge of stock markets?", options: ["Beginner — just started learning", "Basic — understand fundamentals", "Intermediate — follow markets regularly", "Advanced — active investor"] },
  { id: 17, section: "D", text: "Have you experienced a major market downturn with money invested?", options: ["No experience", "Yes — panicked and sold", "Yes — stayed invested", "Yes — bought more during crash"] },
  { id: 18, section: "E", text: "What % of your total savings are you investing here?", options: ["50%+", "25-50%", "10-25%", "Under 10%"] },
  { id: 19, section: "E", text: "Can you afford to lose up to 30% of this investment temporarily?", options: ["No — would severely impact lifestyle", "Maybe — would be difficult", "Yes — manageable", "Definitely — this is long term money"] },
  { id: 20, section: "E", text: "Are you investing borrowed money?", options: ["Yes", "Partially", "No"] },
];

function KYCStep3({ token, onNext, onBack }) {
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const sections = { A: "Personal & Financial Profile", B: "Investment Horizon & Goals", C: "Risk Tolerance", D: "Investment Knowledge", E: "Suitability Check" };

  const totalScore = Object.entries(answers).reduce((sum, [qid, ans]) => {
    const q = RISK_QUESTIONS.find(q => q.id === parseInt(qid));
    return q ? sum + (q.options.indexOf(ans) + 1) : sum;
  }, 0);
  const maxScore = RISK_QUESTIONS.reduce((sum, q) => sum + q.options.length, 0);
  const pct = Math.round((totalScore / maxScore) * 80);
  const getRiskLabel = () => pct <= 40 ? { label: "Conservative", color: T.green } : pct <= 60 ? { label: "Moderate", color: "#F59E0B" } : { label: "Aggressive", color: T.red };
  const allAnswered = RISK_QUESTIONS.every(q => answers[q.id]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const res = await apiFetch("/kyc/risk-profile", { method: "POST", body: JSON.stringify({ responses: answers, total_score: pct }) }, token);
      if (res.success) onNext(res); else alert("Error saving risk profile");
    } catch(e) { alert(e.message); } finally { setSaving(false); }
  };

  return (
    <Card>
      <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>Risk Profile Questionnaire</div>
      <div style={{ fontSize: 12, color: T.text2, marginBottom: 20 }}>Answer all 20 questions honestly. This determines the right strategy for you.</div>

      {Object.entries(sections).map(([sec, secLabel]) => (
        <div key={sec} style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.blue, marginBottom: 12, paddingBottom: 6, borderBottom: `0.5px solid ${T.border}` }}>
            Section {sec}: {secLabel}
          </div>
          {RISK_QUESTIONS.filter(q => q.section === sec).map(q => (
            <div key={q.id} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>{q.id}. {q.text}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {q.options.map((opt, i) => (
                  <label key={i} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "6px 10px", borderRadius: 6,
                    background: answers[q.id] === opt ? T.blueBg : "transparent", border: `0.5px solid ${answers[q.id] === opt ? T.blue : T.border}` }}>
                    <input type="radio" name={`q${q.id}`} value={opt} checked={answers[q.id] === opt}
                      onChange={() => setAnswers(a => ({ ...a, [q.id]: opt }))} style={{ accentColor: T.blue }} />
                    <span style={{ fontSize: 12 }}>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}

      {allAnswered && (
        <div style={{ padding: 16, borderRadius: 10, background: T.blueBg, border: `1px solid ${T.blue}`, marginBottom: 20, textAlign: "center" }}>
          <div style={{ fontSize: 13, color: T.text2, marginBottom: 4 }}>Your risk profile</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: getRiskLabel().color }}>{getRiskLabel().label}</div>
          <div style={{ fontSize: 12, color: T.text2, marginTop: 4 }}>Score: {pct}/80</div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
        <Btn onClick={onBack}>← Back</Btn>
        <Btn variant="primary" onClick={handleSubmit} disabled={!allAnswered || saving}>
          {!allAnswered ? `Answer all (${Object.keys(answers).length}/20)` : saving ? "Saving..." : "Save & Continue →"}
        </Btn>
      </div>
    </Card>
  );
}

function KYCStep4({ token, profile, riskProfile, onNext, onBack }) {
  const [mitc, setMitc] = useState(false);
  const [ai, setAi] = useState(false);
  const [fee, setFee] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [cooling, setCooling] = useState(false);
  const [charter, setCharter] = useState(false);
  const [saving, setSaving] = useState(false);

  const allAccepted = mitc && ai && fee && conflict && cooling && charter;

  const handleSubmit = async () => {
    if (!allAccepted) { alert("Please accept all terms to proceed"); return; }
    setSaving(true);
    try {
      const res = await apiFetch("/kyc/agreement", { method: "POST", body: JSON.stringify({
        mitc_accepted: true, ai_disclosure_accepted: true, fee_structure_accepted: true
      }) }, token);
      if (res.success) onNext(); else alert("Error: " + res.message);
    } catch(e) { alert(e.message); } finally { setSaving(false); }
  };

  const Check = ({ checked, onChange, children }) => (
    <label style={{ display: "flex", gap: 10, cursor: "pointer", marginBottom: 14, padding: 14, borderRadius: 8,
      border: `0.5px solid ${checked ? T.green : T.border}`, background: checked ? "#F0FDF4" : T.bg }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ marginTop: 2, accentColor: T.green, flexShrink: 0 }} />
      <span style={{ fontSize: 12, lineHeight: 1.6 }}>{children}</span>
    </label>
  );

  const Section = ({ title, children }) => (
    <div style={{ padding: 14, borderRadius: 8, background: T.bg2, marginBottom: 14, border: `0.5px solid ${T.border}`, fontSize: 12, lineHeight: 1.8 }}>
      <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>{title}</div>
      {children}
    </div>
  );

  const riskCat = riskProfile?.risk_category || "Moderate";
  const strategyMap = { Conservative: "low-volatility ETFs and debt-heavy allocation", Moderate: "Nifty 200 Momentum strategy", Aggressive: "high-conviction momentum and growth strategy" };

  return (
    <Card>
      <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>Advisory Agreement & Disclosures</div>
      <div style={{ fontSize: 12, color: T.text2, marginBottom: 20 }}>Please read all sections carefully and accept each disclosure before submitting</div>

      {/* 1. SEBI Registration */}
      <Section title="1. SEBI Registration & Identity">
        <div>• <b>Advisor:</b> Rohit M R, ThinkWealth Advisory</div>
        <div>• <b>SEBI RIA Registration:</b> Application in progress — expected July 2026. Currently operating in testing mode with known clients only, as permitted under SEBI guidelines for pre-registration advisory.</div>
        <div>• <b>BASL Membership:</b> To be obtained upon SEBI registration. <i>[Placeholder — will be updated: BASL/RIA/XXXXXX]</i></div>
        <div>• <b>Registered office:</b> Indore, Madhya Pradesh, India</div>
        <div>• <b>Contact:</b> rohimr@gmail.com</div>
      </Section>

      {/* 2. Conflict of Interest */}
      <Section title="2. Conflict of Interest Disclosure">
        <div>• ThinkWealth operates on a <b>fee-only model</b>. We do not receive any commissions, referral fees, or incentives from any broker, AMC, or financial product provider.</div>
        <div>• We do not distribute mutual funds, insurance, or any financial products.</div>
        <div>• The advisor may personally invest in the same securities recommended to clients. All such positions will be disclosed on request.</div>
        <div>• There are no related-party transactions or undisclosed business relationships that could create a conflict of interest.</div>
        <div>• <b>We are obligated to act in your best interest at all times.</b></div>
      </Section>

      {/* 3. MITC */}
      <Section title="3. Most Important Terms & Conditions (MITC)">
        <div>• ThinkWealth provides <b>investment advisory services only</b> — we do not execute trades on your behalf without your explicit consent for each individual trade.</div>
        <div>• You retain full control of your broker account. All orders are placed by you.</div>
        <div>• ThinkWealth does not hold your funds or securities at any point.</div>
        <div>• All advice is based on your risk profile and suitability assessment. Past performance does not guarantee future returns.</div>
        <div>• You can terminate this advisory relationship with <b>30 days written notice</b>. Fees paid for the current quarter will be pro-rated and refunded.</div>
        <div>• Records of all advice and interactions are maintained for <b>5 years</b> as per SEBI IA Regulations 2013.</div>
        <div>• Grievances: rohimr@gmail.com. Unresolved grievances may be escalated to SEBI SCORES at scores.sebi.gov.in</div>
      </Section>

      {/* 4. Suitability */}
      <Section title="4. Suitability Assessment & Client Categorisation">
        <div>• Based on your risk questionnaire, your profile is: <b>{riskCat}</b></div>
        <div>• The recommended strategy for your profile is: <b>{strategyMap[riskCat] || "Nifty 200 Momentum"}</b></div>
        <div>• You are categorised as a <b>Retail Individual Client</b> as per SEBI IA Regulations.</div>
        <div>• This suitability assessment will be reviewed annually or upon material change in your financial circumstances.</div>
        <div>• If your circumstances change significantly, please notify us immediately so we can reassess suitability.</div>
      </Section>

      {/* 5. Limitation of Liability */}
      <Section title="5. Limitation of Liability & Risk Disclosure">
        <div>• Investment in securities markets is subject to market risks. <b>Read all related documents carefully before investing.</b></div>
        <div>• ThinkWealth's advice is based on publicly available information and proprietary analysis. We make no guarantee of returns.</div>
        <div>• ThinkWealth shall not be liable for losses arising from market volatility, force majeure events, broker errors, or client's failure to execute recommended trades timely.</div>
        <div>• Equity investments may result in loss of principal. Past performance of any strategy is not indicative of future results.</div>
        <div>• ThinkWealth is not responsible for tax implications of investments. Please consult your tax advisor.</div>
      </Section>

      {/* 6. Fee Structure */}
      <Section title="6. Fee Structure">
        <div>• <b>Advisory fee:</b> Up to 2.5% per annum on Assets Under Advisory (AUA), billed quarterly in advance.</div>
        <div>• <b>No commissions</b> — ThinkWealth does not earn any trail commission, brokerage sharing, or product referral fees.</div>
        <div>• <b>No performance fees</b> — performance-based fees are not permitted for SEBI RIAs.</div>
        <div>• Fee invoices will be sent quarterly. Payment within 15 days of invoice date.</div>
        <div>• AUA is calculated as the market value of the portfolio on the last trading day of each quarter.</div>
      </Section>

      {/* 7. Cooling-Off Period */}
      <Section title="7. Cooling-Off Period">
        <div>• You have the right to cancel this advisory agreement within <b>24 hours of signing</b> without any penalty or charge.</div>
        <div>• To exercise this right, send an email to rohimr@gmail.com within 24 hours of the timestamp on this agreement.</div>
        <div>• After the cooling-off period, termination requires 30 days written notice as per the MITC above.</div>
      </Section>

      {/* 8. Investor Charter */}
      <Section title="8. Investor Charter (SEBI Mandated)">
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Your Rights as an Investor:</div>
        <div>• Receive honest, unbiased, and suitable investment advice</div>
        <div>• Be informed of all fees, charges, and conflicts of interest</div>
        <div>• Receive a copy of your KYC documents and risk assessment</div>
        <div>• Terminate the advisory relationship at any time with notice</div>
        <div>• Have your grievances addressed within 30 days</div>
        <div>• Escalate unresolved grievances to SEBI SCORES</div>
        <div style={{ fontWeight: 600, margin: "8px 0 4px" }}>Your Responsibilities:</div>
        <div>• Provide accurate and complete personal and financial information</div>
        <div>• Inform the advisor of material changes in financial circumstances</div>
        <div>• Read all advisory communications carefully before acting</div>
        <div>• Execute trades through your own broker account</div>
        <div>• Pay advisory fees on time</div>
        <div style={{ marginTop: 8, fontSize: 11, color: T.text2 }}>
          For more information on investor rights, visit: <b>investor.sebi.gov.in</b>
        </div>
      </Section>

      {/* 9. AI & Data Disclosure */}
      <Section title="9. AI Usage & Data Privacy Disclosure">
        <div>• ThinkWealth uses AI tools including <b>Claude by Anthropic</b> to assist in research, analysis, report generation, and platform development.</div>
        <div>• <b>All investment decisions and advice remain the sole responsibility of the human advisor</b> — Rohit M R. AI is a tool, not the advisor.</div>
        <div>• Your personal and financial data is stored securely on AWS infrastructure (Mumbai region) with encryption at rest and in transit.</div>
        <div>• KYC documents are stored in encrypted AWS S3 with access controls. Documents are retained for 5 years as per SEBI requirements.</div>
        <div>• Your data is never sold or shared with third parties except as required by law or regulation.</div>
      </Section>

      {/* Checkboxes */}
      <div style={{ marginTop: 20 }}>
        <Check checked={mitc} onChange={setMitc}>
          <b>I have read and accept the MITC, Suitability Assessment, Limitation of Liability, and Fee Structure</b> as described in sections 3, 4, 5, and 6 above.
        </Check>
        <Check checked={conflict} onChange={setConflict}>
          <b>Conflict of Interest:</b> I acknowledge that ThinkWealth operates on a fee-only model with no commissions or hidden incentives, and is obligated to act in my best interest.
        </Check>
        <Check checked={cooling} onChange={setCooling}>
          <b>Cooling-Off Period:</b> I understand I have 24 hours from signing to cancel this agreement without penalty by emailing rohimr@gmail.com.
        </Check>
        <Check checked={charter} onChange={setCharter}>
          <b>Investor Charter:</b> I have read and understood my rights and responsibilities as an investor as described in section 8 above.
        </Check>
        <Check checked={ai} onChange={setAi}>
          <b>AI & Data Privacy:</b> I understand and consent to ThinkWealth's use of AI tools and data storage practices as described in section 9 above.
        </Check>
        <Check checked={fee} onChange={setFee}>
          <b>Fee Agreement:</b> I agree to the advisory fee structure — up to 2.5% per annum on AUA, billed quarterly, with no commissions or performance fees.
        </Check>
      </div>

      <div style={{ padding: 12, borderRadius: 8, background: T.blueBg, fontSize: 11, color: T.text2, marginBottom: 20, lineHeight: 1.6 }}>
        By submitting, you confirm that all information provided is accurate and complete, and that you have read and accepted all disclosures above. This constitutes a legally valid digital consent as per the Information Technology Act, 2000.<br/>
        <b>Timestamp:</b> {new Date().toLocaleString("en-IN")} &nbsp;|&nbsp; <b>IP:</b> Captured server-side
      </div>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Btn onClick={onBack}>← Back</Btn>
        <Btn variant="primary" onClick={handleSubmit} disabled={!allAccepted || saving}>
          {saving ? "Submitting..." : allAccepted ? "Submit KYC →" : `Accept all (${[mitc,ai,fee,conflict,cooling,charter].filter(Boolean).length}/6)`}
        </Btn>
      </div>
    </Card>
  );
}

function KYCComplete({ profile }) {
  return (
    <div style={{ textAlign: "center", padding: "40px 20px" }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
      <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>KYC Submitted!</div>
      <div style={{ fontSize: 13, color: T.text2, marginBottom: 24, lineHeight: 1.8 }}>
        Your KYC documents and risk profile have been submitted successfully.<br/>
        Your advisor will review and verify within 1-2 business days.<br/>
        You will be notified once your KYC is approved.
      </div>
      {profile?.kyc_status === "verified" && (
        <div style={{ padding: 16, borderRadius: 10, background: "#F0FDF4", border: `1px solid ${T.green}`, color: T.green, fontSize: 14, fontWeight: 500 }}>
          ✓ KYC Verified — You are ready to invest!
        </div>
      )}
      {profile?.kyc_status === "submitted" && (
        <div style={{ padding: 16, borderRadius: 10, background: T.blueBg, border: `1px solid ${T.blue}`, color: T.blueText, fontSize: 14 }}>
          ⏳ Under review — We will notify you within 1-2 business days
        </div>
      )}
    </div>
  );
}

// ─── ADMIN KYC REVIEW PAGE ────────────────────────────────────────────────────
function ComplianceReportBtn({ token }) {
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState(null);
  const generate = async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/reports/compliance-audit", {}, token);
      setUrl(res.url);
    } catch(e) { alert("Failed: " + e.message); }
    finally { setLoading(false); }
  };
  if (url) return <a href={url} target="_blank" rel="noreferrer"
    style={{ fontSize: 12, padding: "8px 14px", borderRadius: 8, background: T.green, color: "#fff",
      textDecoration: "none", fontWeight: 500 }}>📋 Download Audit</a>;
  return <Btn style={{ fontSize: 12 }} onClick={generate} disabled={loading}>
    {loading ? "Generating…" : "📋 Compliance Audit"}
  </Btn>;
}

function KYCReportBtn({ token, userId }) {
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState(null);
  const generate = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/reports/kyc-summary/${userId}`, {}, token);
      setUrl(res.url);
    } catch(e) { alert("Failed: " + e.message); }
    finally { setLoading(false); }
  };
  if (url) return <a href={url} target="_blank" rel="noreferrer"
    style={{ fontSize: 12, padding: "7px 12px", borderRadius: 8, background: T.green, color: "#fff",
      textDecoration: "none", fontWeight: 500 }}>📄 Download KYC</a>;
  return <Btn style={{ fontSize: 12 }} onClick={generate} disabled={loading}>
    {loading ? "Generating…" : "📄 KYC PDF"}
  </Btn>;
}

function AdminKYCPage({ token, isMobile }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [clientDocs, setClientDocs] = useState({});
  const [clientRisk, setClientRisk] = useState(null);
  const [onboardModal, setOnboardModal] = useState(null); // portfolio_id of client being onboarded
  const [onboardAmount, setOnboardAmount] = useState("");
  const [onboardLoading, setOnboardLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(null); // portfolio_id being imported

  useEffect(() => {
    apiFetch("/admin/kyc/clients", {}, token)
      .then(d => setClients(d.clients || [])).catch(console.error).finally(() => setLoading(false));
  }, [token]);

  const loadDocs = async (userId) => {
    if (selected === userId) { setSelected(null); setClientDocs({}); setClientRisk(null); return; }
    setSelected(userId);
    const [docsRes, riskRes] = await Promise.all([
      apiFetch(`/kyc/documents?user_id=${userId}`, {}, token),
      apiFetch(`/kyc/risk-profile?user_id=${userId}`, {}, token),
    ]);
    setClientDocs(docsRes.documents || {});
    setClientRisk(riskRes.profile || null);
  };

  const handleOnboard = async () => {
    const amount = parseFloat(onboardAmount);
    if (!amount || amount <= 0) { alert("Please enter a valid lumpsum amount"); return; }
    setOnboardLoading(true);
    try {
      const res = await apiFetch(`/admin/portfolio/${onboardModal}/onboard`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lumpsum: amount }),
      }, token);
      if (res.success) {
        alert(`✅ ${res.message}`);
        setOnboardModal(null); setOnboardAmount("");
      } else { alert("Error: " + (res.detail || "Unknown error")); }
    } catch(e) { alert("Error: " + e.message); }
    finally { setOnboardLoading(false); }
  };

  const handleImportZerodha = async (portfolioId, clientName) => {
    if (!window.confirm(`Import ${clientName}'s strategy holdings from Zerodha? This will overwrite any existing snapshot for today.`)) return;
    setImportLoading(portfolioId);
    try {
      const res = await apiFetch(`/admin/portfolio/${portfolioId}/import-zerodha`, { method: "POST" }, token);
      if (res.success) {
        alert(`✅ ${res.message}${res.skipped_symbols?.length ? `

Ignored non-strategy stocks: ${res.skipped_symbols.join(", ")}` : ""}`);
      } else { alert("Error: " + (res.detail || "Unknown error")); }
    } catch(e) { alert("Error: " + e.message); }
    finally { setImportLoading(null); }
  };

  const handleMarkExecuted = async (portfolioId, clientName) => {
    // First get the pending basket id for this portfolio
    try {
      const res = await apiFetch(`/broker/basket/${portfolioId}`, {}, token);
      const basket = res.basket;
      if (!basket) { alert(`No pending basket found for ${clientName}. Generate a basket first via Onboard Fresh.`); return; }
      if (!window.confirm(`Mark ${clientName}'s basket as manually executed? Do this only after the client confirms they have placed all orders on Zerodha.`)) return;
      const r2 = await apiFetch(`/admin/basket/${basket.basket_id}/mark-executed`, { method: "POST" }, token);
      if (r2.success) alert(`✅ ${r2.message}`);
      else alert("Error: " + (r2.detail || "Unknown"));
    } catch(e) { alert("Error: " + e.message); }
  };

  const verifyKYC = async (userId, status) => {
    await apiFetch(`/admin/kyc/${userId}/verify`, { method: "POST", body: JSON.stringify({ status }) }, token);
    setClients(c => c.map(cl => cl.id === userId ? { ...cl, kyc_status: status } : cl));
  };

  const markIPV = async (userId) => {
    const notes = prompt("IPV notes (optional):");
    await apiFetch(`/admin/kyc/${userId}/ipv-complete`, { method: "POST", body: JSON.stringify({ notes: notes || "Video IPV completed" }) }, token);
    setClients(c => c.map(cl => cl.id === userId ? { ...cl, ipv_status: "completed" } : cl));
    alert("IPV marked complete!");
  };

  const statusColor = (s) => ({ pending: T.text2, in_progress: "#F59E0B", submitted: T.blue, verified: T.green, rejected: T.red })[s] || T.text2;

  if (loading) return <Spinner />;

  return (
    <>
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 500 }}>KYC Review</div>
        <ComplianceReportBtn token={token} />
      </div>
      {clients.length === 0 && <div style={{ color: T.text2, fontSize: 13 }}>No clients onboarded yet.</div>}
      {clients.map(c => (
        <Card key={c.id} style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
            <div>
              <div style={{ fontWeight: 500, fontSize: 14 }}>{c.full_name}</div>
              <div style={{ fontSize: 12, color: T.text2 }}>{c.email} · {c.mobile || "—"}</div>
              <div style={{ fontSize: 12, marginTop: 6, display: "flex", flexWrap: "wrap", gap: "4px 16px" }}>
                <span>KYC: <b style={{ color: statusColor(c.kyc_status) }}>{c.kyc_status || "not started"}</b></span>
                <span>IPV: <b style={{ color: c.ipv_status === "completed" ? T.green : T.text2 }}>{c.ipv_status || "pending"}</b></span>
                <span>Risk: <b>{c.risk_category || "not done"}</b></span>
                <span>Docs: <b>{c.docs_uploaded || 0}</b></span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Btn style={{ fontSize: 12 }} onClick={() => loadDocs(c.id)}>{selected === c.id ? "Hide docs" : "View docs"}</Btn>
              {c.kyc_status === "submitted" && (
                <>
                  <Btn style={{ fontSize: 12, color: T.green, border: `1px solid ${T.green}`, background: "transparent" }} onClick={() => verifyKYC(c.id, "verified")}>✓ Verify</Btn>
                  <Btn style={{ fontSize: 12, color: T.red, border: `1px solid ${T.red}`, background: "transparent" }} onClick={() => verifyKYC(c.id, "rejected")}>✗ Reject</Btn>
                </>
              )}
              {c.ipv_status !== "completed" && c.kyc_status !== "pending" && (
                <Btn style={{ fontSize: 12, background: T.blue, color: "#fff" }} onClick={() => markIPV(c.id)}>📹 Mark IPV</Btn>
              )}
              <KYCReportBtn token={token} userId={c.id} />
              {c.kyc_status === "verified" && (
                <>
                  <Btn style={{ fontSize: 12, background: T.green, color: "#fff" }}
                    onClick={() => { setOnboardModal(c.portfolio_id || c.id); setOnboardAmount(""); }}>
                    ₹ Onboard Fresh
                  </Btn>
                  <Btn style={{ fontSize: 12, background: T.blue, color: "#fff" }}
                    disabled={importLoading === (c.portfolio_id || c.id)}
                    onClick={() => handleImportZerodha(c.portfolio_id || c.id, c.full_name)}>
                    {importLoading === (c.portfolio_id || c.id) ? "Importing…" : "⬇ Import Zerodha"}
                  </Btn>
                  <Btn style={{ fontSize: 12, background: "#6B7280", color: "#fff" }}
                    onClick={() => handleMarkExecuted(c.portfolio_id || c.id, c.full_name)}>
                    ✓ Mark Executed
                  </Btn>
                </>
              )}
            </div>
          </div>

          {selected === c.id && (
            <div style={{ marginTop: 16, borderTop: `0.5px solid ${T.border}`, paddingTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>Documents</div>
              {Object.keys(clientDocs).length === 0 && <div style={{ fontSize: 12, color: T.text2 }}>No documents uploaded yet</div>}
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 10 }}>
                {Object.entries(clientDocs).map(([type, doc]) => (
                  <div key={type} style={{ padding: 10, borderRadius: 8, border: `0.5px solid ${T.border}`, fontSize: 12 }}>
                    <div style={{ fontWeight: 500, textTransform: "capitalize", marginBottom: 4 }}>{type.replace("_", " ")}</div>
                    <div style={{ color: T.text2, marginBottom: 6 }}>{doc.file_name}</div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <a href={doc.download_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: T.blue }}>Download</a>
                      <Badge type={doc.status === "verified" ? "green" : doc.status === "rejected" ? "red" : "amber"}>{doc.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>

              {/* Risk Profile Responses */}
              <div style={{ marginTop: 20, borderTop: `0.5px solid ${T.border}`, paddingTop: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>
                  Risk Profile
                  {clientRisk && <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 400, color: T.text2 }}>
                    Score: <b>{clientRisk.total_score}</b> · Category: <b style={{ color: T.green }}>{clientRisk.risk_category}</b>
                    {clientRisk.completed_at && <span> · Completed {new Date(clientRisk.completed_at).toLocaleDateString("en-IN")}</span>}
                  </span>}
                </div>
                {!clientRisk && <div style={{ fontSize: 12, color: T.text2 }}>Risk questionnaire not completed yet</div>}
                {clientRisk?.responses && (() => {
                  const sections = { A: "Personal & Financial Profile", B: "Investment Horizon & Goals", C: "Risk Tolerance", D: "Investment Knowledge", E: "Suitability Check" };
                  return Object.entries(sections).map(([sec, secLabel]) => {
                    const qs = RISK_QUESTIONS.filter(q => q.section === sec);
                    return (
                      <div key={sec} style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: T.text2, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
                          {sec}. {secLabel}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 6 }}>
                          {qs.map(q => (
                            <div key={q.id} style={{ padding: "10px 12px", borderRadius: 8, background: T.bg2, border: `0.5px solid ${T.border}`, fontSize: 12 }}>
                              <div style={{ color: T.text2, marginBottom: 4, lineHeight: 1.4 }}>
                                <span style={{ fontWeight: 600, marginRight: 4 }}>Q{q.id}.</span>{q.text}
                              </div>
                              <div style={{ color: T.text, fontWeight: 500, background: T.bg, borderRadius: 4, padding: "3px 8px", display: "inline-block" }}>
                                {clientRisk.responses[String(q.id)] || "—"}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>

            </div>
          )}
        </Card>
      ))}
    </div>

    {/* ── Onboard Fresh Client Modal ── */}
    {onboardModal && (
      <div style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20
      }}>
        <div style={{ background: T.card, borderRadius: 16, padding: 28, width: "100%", maxWidth: 420, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Onboard Fresh Client</div>
          <div style={{ fontSize: 12, color: T.text2, marginBottom: 20, lineHeight: 1.6 }}>
            Enter the lumpsum amount this client is investing. The system will generate BUY orders
            proportional to your latest published strategy weights.
          </div>
          <label style={{ fontSize: 12, color: T.text2, display: "block", marginBottom: 6 }}>
            Lumpsum Amount (₹)
          </label>
          <input
            type="number"
            value={onboardAmount}
            onChange={e => setOnboardAmount(e.target.value)}
            placeholder="e.g. 500000"
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 14,
              border: `1px solid ${T.border}`, background: T.bg, color: T.text,
              marginBottom: 8, boxSizing: "border-box"
            }}
          />
          {onboardAmount && parseFloat(onboardAmount) > 0 && (
            <div style={{ fontSize: 11, color: T.text2, marginBottom: 16 }}>
              ₹{parseFloat(onboardAmount).toLocaleString("en-IN")} will be apportioned across all strategy stocks
            </div>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn onClick={() => { setOnboardModal(null); setOnboardAmount(""); }}>Cancel</Btn>
            <Btn variant="primary" onClick={handleOnboard} disabled={onboardLoading || !onboardAmount}>
              {onboardLoading ? "Creating basket…" : "Generate Basket →"}
            </Btn>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// ─── APP ─────────────────────────────────────────────────────────────────────
// ─── CLIENT: RAISE GRIEVANCE ─────────────────────────────────────────────────
function GrievancePage({ token }) {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("normal");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    apiFetch("/grievances/my", {}, token)
      .then(d => setHistory(d.grievances || [])).catch(console.error);
  }, [token]);

  const submit = async () => {
    if (!subject.trim() || !description.trim()) { alert("Please fill subject and description"); return; }
    setSubmitting(true);
    try {
      await apiFetch("/grievances/submit", {
        method: "POST",
        body: JSON.stringify({ subject, description, priority })
      }, token);
      setSubmitted(true);
      setSubject(""); setDescription(""); setPriority("normal");
      // Refresh history
      const d = await apiFetch("/grievances/my", {}, token);
      setHistory(d.grievances || []);
    } catch(e) { alert(e.message); }
    finally { setSubmitting(false); }
  };

  const statusColor = s => ({ open: T.amber, in_progress: T.blue, resolved: T.green, closed: T.text2 })[s] || T.text2;

  return (
    <div>
      {submitted && (
        <div style={{ padding: "12px 16px", background: T.greenBg, borderRadius: 10, marginBottom: 16,
          fontSize: 13, color: T.greenText, border: `0.5px solid ${T.green}` }}>
          ✓ Grievance submitted — your advisor will respond within 30 days. You'll receive a confirmation email.
        </div>
      )}

      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Submit a grievance</div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: T.text2, display: "block", marginBottom: 6 }}>Subject</label>
          <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Brief description of your concern"
            style={{ width: "100%", padding: "10px 12px", border: `0.5px solid ${T.border2}`, borderRadius: 8,
              background: T.bg, color: T.text, fontSize: 16, boxSizing: "border-box" }} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: T.text2, display: "block", marginBottom: 6 }}>Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)}
            placeholder="Please describe your concern in detail..." rows={4}
            style={{ width: "100%", padding: "10px 12px", border: `0.5px solid ${T.border2}`, borderRadius: 8,
              background: T.bg, color: T.text, fontSize: 14, boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" }} />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, color: T.text2, display: "block", marginBottom: 6 }}>Priority</label>
          <div style={{ display: "flex", gap: 8 }}>
            {[["low", "Low"], ["normal", "Normal"], ["high", "High"], ["urgent", "Urgent"]].map(([val, label]) => (
              <button key={val} onClick={() => setPriority(val)}
                style={{ flex: 1, padding: "7px 4px", borderRadius: 8, fontSize: 12, cursor: "pointer",
                  border: `0.5px solid ${priority === val ? T.text : T.border}`,
                  background: priority === val ? T.text : "transparent",
                  color: priority === val ? T.bg : T.text2 }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <Btn variant="primary" style={{ width: "100%", padding: "12px" }} onClick={submit} disabled={submitting}>
          {submitting ? "Submitting…" : "Submit grievance"}
        </Btn>

        <div style={{ marginTop: 14, padding: "10px 12px", background: T.bg2, borderRadius: 8, fontSize: 11, color: T.text2, lineHeight: 1.6 }}>
          As per SEBI IA Regulations, all grievances will be addressed within 30 days.
          For unresolved issues, you may escalate to the SEBI SCORES portal at scores.sebi.gov.in
        </div>
      </Card>

      {history.length > 0 && (
        <Card>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>My grievances</div>
          {history.map(g => (
            <div key={g.id} style={{ padding: "12px 0", borderBottom: `0.5px solid ${T.border}` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{g.subject}</div>
                <span style={{ fontSize: 11, fontWeight: 600, color: statusColor(g.status), textTransform: "uppercase" }}>{g.status}</span>
              </div>
              <div style={{ fontSize: 11, color: T.text2, marginBottom: g.resolution ? 6 : 0 }}>
                Submitted {g.created_at} · Priority: {g.priority}
              </div>
              {g.resolution && (
                <div style={{ fontSize: 12, color: T.green, marginTop: 6, padding: "8px 10px", background: T.greenBg, borderRadius: 6 }}>
                  Resolution: {g.resolution}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ─── ADMIN: GRIEVANCE MANAGEMENT ─────────────────────────────────────────────
function AdminGrievancePage({ token }) {
  const [grievances, setGrievances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);
  const [resolution, setResolution] = useState("");

  useEffect(() => {
    apiFetch("/admin/grievances", {}, token)
      .then(d => setGrievances(d.grievances || [])).catch(console.error).finally(() => setLoading(false));
  }, [token]);

  const resolve = async (id, status) => {
    if (!resolution.trim()) { alert("Please enter a resolution note"); return; }
    try {
      await apiFetch(`/admin/grievances/${id}/resolve`, {
        method: "POST", body: JSON.stringify({ status, resolution })
      }, token);
      setGrievances(g => g.map(x => x.id === id ? { ...x, status, resolution } : x));
      setResolving(null); setResolution("");
    } catch(e) { alert(e.message); }
  };

  const statusColor = s => ({ open: T.red, in_progress: T.amber, resolved: T.green, closed: T.text2 })[s] || T.text2;
  const priorityColor = p => ({ urgent: T.red, high: T.amber, normal: T.blue, low: T.text2 })[p] || T.text2;

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ fontSize: 14, color: T.text2, marginBottom: 20 }}>
        {grievances.length} total · {grievances.filter(g => g.status === "open").length} open
      </div>

      {grievances.length === 0 && (
        <Card>
          <div style={{ fontSize: 13, color: T.text2, textAlign: "center", padding: "20px 0" }}>
            No grievances submitted yet.
          </div>
        </Card>
      )}

      {grievances.map(g => (
        <Card key={g.id} style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>{g.subject}</div>
              <div style={{ fontSize: 11, color: T.text2 }}>{g.client_name} · {g.client_email} · {g.created_at}</div>
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: priorityColor(g.priority), textTransform: "uppercase" }}>{g.priority}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: statusColor(g.status), textTransform: "uppercase" }}>{g.status}</span>
            </div>
          </div>

          <div style={{ fontSize: 12, color: T.text2, marginBottom: 10, lineHeight: 1.6, padding: "8px 10px", background: T.bg2, borderRadius: 6 }}>
            {g.description}
          </div>

          {g.resolution && (
            <div style={{ fontSize: 12, color: T.green, padding: "8px 10px", background: T.greenBg, borderRadius: 6, marginBottom: 10 }}>
              Resolution: {g.resolution}
            </div>
          )}

          {g.status === "open" || g.status === "in_progress" ? (
            resolving === g.id ? (
              <div>
                <textarea value={resolution} onChange={e => setResolution(e.target.value)}
                  placeholder="Enter resolution details..." rows={2}
                  style={{ width: "100%", padding: "8px 10px", border: `0.5px solid ${T.border2}`, borderRadius: 8,
                    background: T.bg, color: T.text, fontSize: 13, boxSizing: "border-box", resize: "none", fontFamily: "inherit", marginBottom: 8 }} />
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn style={{ fontSize: 12 }} onClick={() => { setResolving(null); setResolution(""); }}>Cancel</Btn>
                  <Btn style={{ fontSize: 12, color: T.amber, border: `1px solid ${T.amber}` }} onClick={() => resolve(g.id, "in_progress")}>Mark in progress</Btn>
                  <Btn variant="primary" style={{ fontSize: 12 }} onClick={() => resolve(g.id, "resolved")}>Mark resolved</Btn>
                </div>
              </div>
            ) : (
              <Btn style={{ fontSize: 12 }} onClick={() => setResolving(g.id)}>Respond →</Btn>
            )
          ) : null}
        </Card>
      ))}
    </div>
  );
}

export default function App() {
  const [auth, setAuth] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tw_auth") || "null"); } catch { return null; }
  });
  // IMPORTANT: page must default based on the already-known auth role, not a
  // hardcoded "dashboard". "dashboard" is an admin-only page — if a client's
  // session is restored from localStorage (or briefly while role is being
  // resolved), defaulting here to "dashboard" causes AdminDashboard to attempt
  // to render for a client, which the backend correctly rejects, surfacing a
  // raw "admin only" error before the redirect below ever kicks in.
  const [page, setPage] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("tw_auth") || "null");
      return saved?.role === "admin" ? "dashboard" : "model_portfolio";
    } catch { return "model_portfolio"; }
  });
  const [selectedPortfolio, setSelectedPortfolio] = useState("4896f468-df39-4e3b-a000-fc480d8634e8");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accessStatus, setAccessStatus] = useState(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (auth && auth.role === "client") {
      apiFetch("/client/portfolios/live", {}, auth.access_token)
        .then(d => { if (d.portfolios?.length > 0) setSelectedPortfolio(d.portfolios[0].id); })
        .catch(console.error);
      apiFetch("/client/access-status", {}, auth.access_token)
        .then(setAccessStatus).catch(console.error);
    }
    // If Zerodha basket callback landed here with a token, auto-navigate to rebalance
    const params = new URLSearchParams(window.location.search);
    if (params.get("request_token") && params.get("status") === "success") {
      setPage("rebalance");
    }
  }, [auth]);

  const handleLogin = (data) => {
    setAuth(data);
    localStorage.setItem("tw_auth", JSON.stringify(data));
    setPage(data.role === "admin" ? "dashboard" : "model_portfolio");
  };

  const handleLogout = () => { setAuth(null); setPage("model_portfolio"); localStorage.removeItem("tw_auth"); };

  // Handle Zerodha callback redirect — works for both OAuth login and basket execution
  // Zerodha redirects to /zerodha-callback?request_token=xxx&status=success
  if (window.location.pathname === "/zerodha-callback") {
    const params = new URLSearchParams(window.location.search);
    const requestToken = params.get("request_token");
    const status = params.get("status");
    if (requestToken && status === "success" && auth) {
      // Redirect to rebalance page with token in URL so RebalancePage can handle it
      window.location.href = `/?request_token=${requestToken}&status=success`;
      return null;
    }
    // If not logged in or no token, go home
    window.location.href = "/";
    return null;
  }

  if (!auth) return <LoginPage onLogin={handleLogin} />;

  const nav = auth.role === "admin" ? ADMIN_NAV : CLIENT_NAV;
  const token = auth.access_token;

  // Close sidebar whenever we navigate (important on mobile)
  const navigate = (id) => { setPage(id); setSidebarOpen(false); };

  const clientLocked = false; // KYC lock temporarily disabled for pre-SEBI testing — re-enable by restoring: auth.role === "client" && !accessStatus?.full_access
  const goKyc = () => navigate("kyc");

  const renderPage = () => {
    switch (page) {
      case "dashboard": return <AdminDashboard token={token} isMobile={isMobile} />;
      case "clients": return <AdminDashboard token={token} isMobile={isMobile} />;
      case "analytics": return <PortfolioAnalytics token={token} portfolioId={selectedPortfolio} isMobile={isMobile} auth={auth} />;
      case "model_portfolio": return <ModelPortfolioPage token={token} accessStatus={accessStatus} onGoKyc={goKyc} />;
      case "portfolio": return clientLocked ? <LockedPage onGoKyc={goKyc} /> : <PortfolioAnalytics token={token} portfolioId={selectedPortfolio} isMobile={isMobile} auth={auth} />;
      case "portfolios": return clientLocked ? <LockedPage onGoKyc={goKyc} /> : <ClientPortfolios token={token} onSelectPortfolio={id => { setSelectedPortfolio(id); setPage("portfolio"); }} isMobile={isMobile} />;
      case "history": return <RebalanceHistoryPage token={token} isMobile={isMobile} />;
      case "kyc": return <KYCPage token={token} auth={auth} isMobile={isMobile} />;
      case "kyc_review": return <AdminKYCPage token={token} isMobile={isMobile} />;
      case "rebalance": return auth.role === "admin" ? (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.text2, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
            PUBLISH REBALANCE — applies to all client accounts
          </div>
          <AdminRebalancePage token={token} auth={auth} isMobile={isMobile} />
          <div style={{ marginTop: 40, paddingTop: 28, borderTop: `2px solid ${T.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.text2, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
              MY PERSONAL EXECUTION BASKET — your own Zerodha account only
            </div>
            <div style={{ fontSize: 12, color: T.text2, marginBottom: 16 }}>
              This section is separate from publishing. Execute trades on your own account independent of what clients see.
            </div>
            <RebalancePage token={token} portfolioId="4896f468-df39-4e3b-a000-fc480d8634e8" auth={auth} isMobile={isMobile} />
          </div>
        </div>
      ) : clientLocked ? <LockedPage onGoKyc={goKyc} /> : <RebalancePage token={token} portfolioId={selectedPortfolio} auth={auth} isMobile={isMobile} />;
      case "sip": return clientLocked ? <LockedPage onGoKyc={goKyc} /> : <SIPPage token={token} />;
      case "grievance": return <GrievancePage token={token} />;
      case "grievances": return <AdminGrievancePage token={token} />;
      case "hedge_intel": return <HedgeIntelligencePage token={token} isMobile={isMobile} />;
      default: return null;
    }
  };

  // Page metadata for headers and bottom nav
  const PAGE_META = {
    dashboard:       { label: "Dashboard",          short: "Home"      },
    clients:         { label: "Clients",             short: "Clients"   },
    rebalance:       { label: "Rebalance",           short: "Rebalance" },
    history:         { label: "History",             short: "History"   },
    kyc_review:      { label: "KYC Review",          short: "KYC"       },
    analytics:       { label: "Performance",         short: "Charts"    },
    kyc:             { label: "KYC & Onboarding",    short: "KYC"       },
    model_portfolio: { label: "Model Portfolio",     short: "Strategy"  },
    portfolio:       { label: "My Portfolio",        short: "Portfolio" },
    portfolios:      { label: "All Portfolios",      short: "Portfolios"},
    grievance:       { label: "Raise Grievance",     short: "Grievance" },
    grievances:      { label: "Grievances",          short: "Grievances" },
    hedge_intel:     { label: "Hedge Intelligence",  short: "Hedge"      },
  };

  const BOTTOM_NAV_ADMIN  = ["dashboard", "rebalance", "history", "kyc_review"];
  const BOTTOM_NAV_CLIENT = ["model_portfolio", "kyc", "rebalance", "sip"];
  const bottomNav = auth.role === "admin" ? BOTTOM_NAV_ADMIN : BOTTOM_NAV_CLIENT;

  const currentMeta = PAGE_META[page] || { label: page, short: page };
  const isDashboard = page === "dashboard" || page === "portfolio";

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: T.bg3, fontFamily: "system-ui, -apple-system, sans-serif" }}>

      {/* ── Dark overlay — tap anywhere to close sidebar on mobile ── */}
      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 150, background: "rgba(0,0,0,0.4)" }} />
      )}

      {/* ── Sidebar — fixed drawer on mobile, static on desktop ── */}
      <div style={{
        width: 200, background: T.bg, borderRight: `0.5px solid ${T.border}`,
        display: "flex", flexDirection: "column", flexShrink: 0,
        ...(isMobile ? {
          position: "fixed", top: 0, left: sidebarOpen ? 0 : -220,
          height: "100vh", zIndex: 160, overflowY: "auto",
          transition: "left 0.25s ease",
          boxShadow: sidebarOpen ? "4px 0 20px rgba(0,0,0,0.15)" : "none",
        } : {})
      }}>
        <div style={{ padding: "16px 20px 12px", borderBottom: `0.5px solid ${T.border}` }}>
          <div>
            <span style={{ fontSize: 17, fontWeight: 600, color: "#2196F3" }}>Think</span>
            <span style={{ fontSize: 17, fontWeight: 600, color: "#00B4A6" }}>Wealth</span>
            <div style={{ height: 2, background: "linear-gradient(to right, #2196F3, #00B4A6)", borderRadius: 2, marginTop: 3 }} />
          </div>
          <div style={{ fontSize: 10, color: T.text2, fontStyle: "italic", marginTop: 3 }}>Money matters, 'coz it shouldn't</div>
        </div>
        <div style={{ padding: "8px 12px 4px", fontSize: 11, color: T.text3, textTransform: "uppercase", letterSpacing: ".05em", marginTop: 8 }}>
          {auth.role === "admin" ? "Admin" : "My account"}
        </div>
        {nav.map(n => (
          <div key={n.id} onClick={() => navigate(n.id)}
            style={{ padding: "9px 20px", fontSize: 13, color: page === n.id ? T.text : T.text2,
              background: page === n.id ? T.bg2 : "transparent", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ opacity: page === n.id ? 1 : 0.6, display: "flex", alignItems: "center" }}>{n.icon}</span>{n.label}
          </div>
        ))}
        <div style={{ marginTop: "auto", padding: "16px 20px", borderTop: `0.5px solid ${T.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <Avatar name={auth.full_name} size={28} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 500 }}>{auth.full_name}</div>
              <div style={{ fontSize: 11, color: T.text2, textTransform: "capitalize" }}>{auth.role}</div>
            </div>
          </div>
          <Btn style={{ width: "100%", fontSize: 12 }} onClick={handleLogout}>Sign out</Btn>
          {!isMobile && (
            <div style={{ marginTop: 12, padding: "10px 0", borderTop: `0.5px solid ${T.border}`, fontSize: 10, color: T.text3, lineHeight: 1.6 }}>
              <div>SEBI RIA: Application in progress</div>
              <div>BASL: Pending registration</div>
              <div style={{ marginTop: 4 }}>
                <a href="https://scores.sebi.gov.in" target="_blank" rel="noreferrer" style={{ color: T.blue, fontSize: 10 }}>SEBI SCORES</a>
                {" · "}
                <a href="https://investor.sebi.gov.in" target="_blank" rel="noreferrer" style={{ color: T.blue, fontSize: 10 }}>Investor Charter</a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Main content area ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>

        {/* ── Mobile top bar ── */}
        {isMobile && (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0, zIndex: 140,
            height: 52, background: T.bg, borderBottom: `0.5px solid ${T.border}`,
            display: "flex", alignItems: "center", paddingLeft: 14, paddingRight: 14, gap: 10,
          }}>
            {/* Logo — tapping takes you home */}
            <div onClick={() => navigate(auth.role === "admin" ? "dashboard" : "portfolio")}
              style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: "#2196F3" }}>Think</span>
              <span style={{ fontSize: 15, fontWeight: 700, color: "#00B4A6" }}>Wealth</span>
            </div>

            {/* Breadcrumb — only when not on home */}
            {!isDashboard && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
                <span style={{ fontSize: 13, color: T.text2, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {currentMeta.label}
                </span>
              </div>
            )}
            {isDashboard && <div style={{ flex: 1 }} />}

            {/* Hamburger — for less-used pages not in bottom nav */}
            <button onClick={() => setSidebarOpen(o => !o)} aria-label="Menu"
              style={{ background: "transparent", border: "none", padding: 6, cursor: "pointer",
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4 }}>
              {[0, 1, 2].map(i => (
                <span key={i} style={{ display: "block", width: 18, height: 1.5, background: T.text, borderRadius: 2, transition: "all 0.2s",
                  transform: sidebarOpen ? (i === 0 ? "rotate(45deg) translate(4px, 4px)" : i === 2 ? "rotate(-45deg) translate(4px, -4px)" : "scaleX(0)") : "none",
                  opacity: sidebarOpen && i === 1 ? 0 : 1 }} />
              ))}
            </button>
          </div>
        )}

        {/* ── Page content ── */}
        <div style={{ flex: 1, padding: isMobile ? "66px 14px 80px" : 24, overflowY: "auto" }}>

          {/* ── Elegant page header — shown on all non-dashboard pages ── */}
          {isMobile && !isDashboard && (
            <div style={{ marginBottom: 20 }}>
              {/* Back to home pill */}
              <div onClick={() => navigate(auth.role === "admin" ? "dashboard" : "portfolio")}
                style={{ display: "inline-flex", alignItems: "center", gap: 5, marginBottom: 14,
                  padding: "4px 10px", borderRadius: 20, background: T.bg2, border: `0.5px solid ${T.border}`,
                  cursor: "pointer", fontSize: 11, color: T.text2 }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                ThinkWealth
              </div>
              {/* Page title */}
              <div style={{ fontSize: 20, fontWeight: 700, color: T.text, letterSpacing: "-0.3px" }}>
                {currentMeta.label}
              </div>
              <div style={{ height: 2, width: 28, background: "linear-gradient(to right, #2196F3, #00B4A6)", borderRadius: 2, marginTop: 6 }} />
            </div>
          )}

          {renderPage()}
        </div>

        {/* ── Bottom tab bar — mobile only ── */}
        {isMobile && (
          <div style={{
            position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 140,
            height: 60, background: T.bg, borderTop: `0.5px solid ${T.border}`,
            display: "flex", alignItems: "stretch",
          }}>
            {bottomNav.map(id => {
              const navItem = nav.find(n => n.id === id);
              const isActive = page === id;
              if (!navItem) return null;
              return (
                <div key={id} onClick={() => navigate(id)}
                  style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                    justifyContent: "center", gap: 4, cursor: "pointer",
                    borderTop: isActive ? `2px solid #2196F3` : "2px solid transparent",
                    background: "transparent" }}>
                  <span style={{ color: isActive ? "#2196F3" : T.text3, display: "flex", transition: "color 0.15s" }}>
                    {navItem.icon}
                  </span>
                  <span style={{ fontSize: 10, color: isActive ? "#2196F3" : T.text3, fontWeight: isActive ? 600 : 400, transition: "color 0.15s" }}>
                    {PAGE_META[id]?.short || navItem.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}
